import React, { useEffect, useMemo, useRef, useState } from "react";
import API from "./api";
import ProfileDetail from "./Profiledetail";
import { getProfileNumberDisplay } from "./profileNumber";
import { hasPermission } from "./rbac";

const initialUser = {
  name: "",
  email: "",
  password: "",
  role_ids: [],
  user_type_id: "",
};

const initialRole = { name: "", description: "", parent_role_id: "" };
const initialPermission = { module: "", action: "", field_key: "", description: "" };
const initialPermissionCatalogFilters = { module: "", action: "", field_key: "" };
const initialFeature = { feature_key: "", module: "", label: "", description: "", sort_order: "" };
const initialUserType = { code: "", name: "", description: "" };
const initialReferralSource = {
  name: "",
  description: "",
  sort_order: "",
  is_trusted: false,
  is_active: true,
};
const initialPolicy = {
  policy_name: "",
  subject_type: "role",
  subject_id: "",
  module: "",
  action: "",
  effect: "restrict",
  priority: 100,
  max_results: 50,
  allowed_state_ids: [],
  allowed_city_ids: [],
  assigned_to_self: false,
  hidden_fields: [],
  condition_json_text: "{\n  \"max_results\": 50,\n  \"hidden_fields\": []\n}",
  show_advanced_json: false,
  is_active: true,
};
const initialPermissionSchema = {};
const initialDropdowns = {};
const initialDropdownTypes = [];
const initialDropdownForm = { name: "", description: "", sort_order: "", parent_id: "" };
const initialCityTierAssignments = {};
const initialProfileVerificationStats = { total: 0, verified: 0, unverified: 0 };
const REFERRAL_SOURCE_DROPDOWN_TYPE = "referral-sources";

const TABS = [
  { id: "dropdowns", label: "Manage Dropdowns" },
  { id: "users", label: "Users" },
  { id: "roles", label: "Roles" },
  { id: "features", label: "Features" },
  { id: "permissions", label: "Permissions" },
  { id: "user_types", label: "User Types" },
  { id: "profile_verification", label: "Profile Verification" },
  { id: "policies", label: "Policies" },
  { id: "diagnostics", label: "Diagnostics" },
];

export default function UserManagement({ token, user }) {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [featurePermissions, setFeaturePermissions] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  const [profileVerificationItems, setProfileVerificationItems] = useState([]);
  const [profileVerificationStats, setProfileVerificationStats] = useState(initialProfileVerificationStats);
  const [policies, setPolicies] = useState([]);
  const [dropdowns, setDropdowns] = useState(initialDropdowns);
  const [dropdownTypes, setDropdownTypes] = useState(initialDropdownTypes);
  const [cityTierAssignments, setCityTierAssignments] = useState(initialCityTierAssignments);
  const [permissionSchema, setPermissionSchema] = useState(initialPermissionSchema);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [selectedUserTypeId, setSelectedUserTypeId] = useState(null);
  const [selectedDropdownType, setSelectedDropdownType] = useState("");
  const [selectedDiagnosticUserId, setSelectedDiagnosticUserId] = useState("");
  const [selectedRoleMeta, setSelectedRoleMeta] = useState(initialRole);
  const [selectedUserTypeMeta, setSelectedUserTypeMeta] = useState(initialUserType);
  const [draftFeatureIds, setDraftFeatureIds] = useState([]);
  const [draftFeaturePermissionIds, setDraftFeaturePermissionIds] = useState([]);
  const [userTypeSavingId, setUserTypeSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState(initialUser);
  const [newRole, setNewRole] = useState(initialRole);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const [assignedProfiles, setAssignedProfiles] = useState([]);
  const [assignmentResults, setAssignmentResults] = useState([]);
  const [assignmentSearch, setAssignmentSearch] = useState({ q: "", search_by: "profile_no" });
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentBusyProfileId, setAssignmentBusyProfileId] = useState(null);
  const [profileVerificationLoading, setProfileVerificationLoading] = useState(false);
  const [profileVerificationBusyId, setProfileVerificationBusyId] = useState(null);
  const [profileDeleteBusyId, setProfileDeleteBusyId] = useState(null);
  const [selectedVerificationProfileId, setSelectedVerificationProfileId] = useState(null);
  const [newPermission, setNewPermission] = useState(initialPermission);
  const [permissionCatalogFilters, setPermissionCatalogFilters] = useState(initialPermissionCatalogFilters);
  const [newFeature, setNewFeature] = useState(initialFeature);
  const [newUserType, setNewUserType] = useState(initialUserType);
  const [newReferralSource, setNewReferralSource] = useState(initialReferralSource);
  const [newPolicy, setNewPolicy] = useState(() => createInitialPolicyDraft());
  const [newDropdown, setNewDropdown] = useState(initialDropdownForm);
  const [dropdownTypeQuery, setDropdownTypeQuery] = useState("");
  const [dropdownItemQuery, setDropdownItemQuery] = useState("");
  const [profileVerificationQuery, setProfileVerificationQuery] = useState("");
  const [profileVerificationStatus, setProfileVerificationStatus] = useState("all");
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsData, setDiagnosticsData] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hierarchyViewMode, setHierarchyViewMode] = useState("tree");
  const profileVerificationListRef = useRef(null);
  const profileVerificationScrollTopRef = useRef(0);

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );
  const canViewUsers = hasPermission(user, "users", "view");
  const canViewRbac = hasPermission(user, "rbac", "view");
  const canCreateUsers = hasPermission(user, "users", "create");
  const canAssignRoles = hasPermission(user, "users", "assign_role");
  const canDeleteUsers = hasPermission(user, "users", "delete");
  const canManageRoles = hasPermission(user, "rbac", "manage_roles");
  const canManagePermissions = hasPermission(user, "rbac", "manage_permissions");
  const canManageFeatures = hasPermission(user, "rbac", "manage_features");
  const canManageFeaturePermissions = hasPermission(user, "rbac", "manage_feature_permissions");
  const canManageUserTypes = hasPermission(user, "rbac", "manage_user_types");
  const canManagePolicies = hasPermission(user, "rbac", "manage_policies");
  const canManageReferralSources = hasPermission(user, "rbac", "manage_referral_sources");
  const canViewProfileVerification = hasPermission(user, "profiles", "verify_view") || hasPermission(user, "profiles", "verify");
  const canVerifyProfiles = hasPermission(user, "profiles", "verify");
  const canOpenVerificationProfile = hasPermission(user, "profiles", "view");
  const canDeleteProfiles = hasPermission(user, "profiles", "delete");
  const canManageDropdowns = canManageFeatures || canManageReferralSources;
  const canAssignProfiles = hasPermission(user, "profiles", "assign");
  const assignableRoleOptions = useMemo(
    () => safeArray(roles),
    [roles]
  );
  const roleHierarchyView = useMemo(() => buildRoleHierarchyView(roles), [roles]);
  const selectedRoleHierarchy = useMemo(
    () => (selectedRoleId ? roleHierarchyView.roleMetaById.get(Number(selectedRoleId)) || null : null),
    [roleHierarchyView, selectedRoleId]
  );
  const selectedRoleCurrentParentId = useMemo(
    () => Number(selectedRoleHierarchy?.parent?.id || 0) || null,
    [selectedRoleHierarchy]
  );
  const selectedRolePath = useMemo(
    () => (selectedRoleId ? roleHierarchyView.getPathToRole(Number(selectedRoleId)) : []),
    [roleHierarchyView, selectedRoleId]
  );
  const selectedRoleUpperParentIds = useMemo(
    () => (selectedRoleCurrentParentId ? roleHierarchyView.getAncestorRoleIds(Number(selectedRoleCurrentParentId)) : []),
    [roleHierarchyView, selectedRoleCurrentParentId]
  );
  const selectedRoleBlockedParentIds = useMemo(() => {
    const blocked = new Set();
    if (!selectedRoleId) return blocked;

    blocked.add(Number(selectedRoleId));
    roleHierarchyView.getDescendantRoleIds(Number(selectedRoleId)).forEach((roleId) => {
      blocked.add(Number(roleId));
    });
    selectedRoleUpperParentIds.forEach((roleId) => {
      blocked.add(Number(roleId));
    });

    return blocked;
  }, [roleHierarchyView, selectedRoleId, selectedRoleUpperParentIds]);
  const selectedRoleParentOptions = useMemo(
    () => safeArray(roles).filter((role) => !selectedRoleBlockedParentIds.has(Number(role.id))),
    [roles, selectedRoleBlockedParentIds]
  );
  const selectedRoleParentGuardMessage = useMemo(() => {
    if (!selectedRoleId) return "";
    if (!selectedRoleCurrentParentId) {
      return "This role is currently at the root level. You can keep it there or move it under another role.";
    }

    const currentParentName = selectedRoleHierarchy?.parent?.name || "its current parent";
    const blockedNames = selectedRoleUpperParentIds
      .map((roleId) => roleHierarchyView.roleMetaById.get(Number(roleId))?.name)
      .filter(Boolean);

    return blockedNames.length
      ? `This role can stay under ${currentParentName} or move deeper, but it cannot move upward to ${blockedNames.join(", ")} or root.`
      : `This role can stay under ${currentParentName} or move deeper, but it cannot move upward to root.`;
  }, [roleHierarchyView, selectedRoleCurrentParentId, selectedRoleHierarchy, selectedRoleId, selectedRoleUpperParentIds]);

  const visibleTabs = useMemo(
    () =>
      TABS.filter((item) => {
        if (item.id === "users") return canViewUsers;
        if (item.id === "roles") return canManageRoles;
        if (item.id === "diagnostics") return canViewRbac && canViewUsers;
        if (item.id === "permissions") return canManagePermissions;
        if (item.id === "features") return canManageFeatures || canManageFeaturePermissions;
        if (item.id === "dropdowns") return canManageDropdowns;
        if (item.id === "profile_verification") return canViewProfileVerification;
        if (item.id === "policies") return canManagePolicies;
        if (item.id === "user_types") return canManageUserTypes;
        return false;
      }),
    [
      canManageFeaturePermissions,
      canManageFeatures,
      canManageDropdowns,
      canViewProfileVerification,
      canManagePolicies,
      canManagePermissions,
      canManageRoles,
      canManageUserTypes,
      canViewUsers,
      canViewRbac,
    ]
  );

  const permissionModules = useMemo(
    () => Object.entries(permissionSchema).map(([value, meta]) => ({ value, label: meta.label || value })),
    [permissionSchema]
  );

  const permissionActions = useMemo(() => {
    const moduleMeta = permissionSchema[newPermission.module];
    if (!moduleMeta?.actions) return [];
    return Object.keys(moduleMeta.actions).map((value) => ({
      value,
      label: formatOptionLabel(value),
    }));
  }, [newPermission.module, permissionSchema]);

  const permissionFieldKeys = useMemo(() => {
    const fieldKeys = permissionSchema[newPermission.module]?.actions?.[newPermission.action] || [];
    return fieldKeys.map((value) => ({
      value,
      label: formatOptionLabel(value),
    }));
  }, [newPermission.action, newPermission.module, permissionSchema]);

  const permissionCatalogActions = useMemo(() => {
    const moduleMeta = permissionSchema[permissionCatalogFilters.module];
    if (!moduleMeta?.actions) return [];
    return Object.keys(moduleMeta.actions).map((value) => ({
      value,
      label: formatOptionLabel(value),
    }));
  }, [permissionCatalogFilters.module, permissionSchema]);

  const permissionCatalogFieldKeys = useMemo(() => {
    const fieldKeys =
      permissionSchema[permissionCatalogFilters.module]?.actions?.[permissionCatalogFilters.action] || [];
    return fieldKeys.map((value) => ({
      value,
      label: formatOptionLabel(value),
    }));
  }, [permissionCatalogFilters.action, permissionCatalogFilters.module, permissionSchema]);

  const filteredPermissions = useMemo(
    () =>
      permissions.filter((permission) => {
        if (permissionCatalogFilters.module && permission.module !== permissionCatalogFilters.module) return false;
        if (permissionCatalogFilters.action && permission.action !== permissionCatalogFilters.action) return false;
        if (permissionCatalogFilters.field_key && permission.field_key !== permissionCatalogFilters.field_key) return false;
        return true;
      }),
    [permissionCatalogFilters.action, permissionCatalogFilters.field_key, permissionCatalogFilters.module, permissions]
  );

  const policyActions = useMemo(() => {
    const moduleMeta = permissionSchema[newPolicy.module];
    if (!moduleMeta?.actions) return [];
    return Object.keys(moduleMeta.actions).map((value) => ({
      value,
      label: formatOptionLabel(value),
    }));
  }, [newPolicy.module, permissionSchema]);

  const statePolicyOptions = useMemo(
    () =>
      safeArray(dropdowns.states)
        .filter((item) => item.is_active !== false)
        .map((item) => ({ value: Number(item.id), label: item.name })),
    [dropdowns.states]
  );

  const cityPolicyOptions = useMemo(
    () =>
      safeArray(dropdowns.cities)
        .filter((item) => item.is_active !== false)
        .map((item) => ({ value: Number(item.id), label: item.name })),
    [dropdowns.cities]
  );

  const dropdownTypesWithReferralSources = useMemo(() => {
    const baseTypes = safeArray(dropdownTypes);
    if (!canManageReferralSources) return baseTypes;

    const activeCount = referralSources.filter((item) => item.is_active !== false).length;
    return [
      ...baseTypes,
      {
        type: REFERRAL_SOURCE_DROPDOWN_TYPE,
        label: "Referral Sources",
        description: "Trusted and non-trusted referral sources used for profile verification.",
        group: "Profile Controls",
        itemCount: referralSources.length,
        activeCount,
        parentLabel: "",
        parentType: null,
        parentField: null,
        supportsDescription: true,
        supportsSortOrder: true,
        supportsActive: true,
        supportsTrusted: true,
      },
    ];
  }, [canManageReferralSources, dropdownTypes, referralSources]);

  const dropdownTypeMap = useMemo(() => buildDropdownTypeMap(dropdownTypesWithReferralSources), [dropdownTypesWithReferralSources]);
  const selectedDropdownMeta = selectedDropdownType ? dropdownTypeMap[selectedDropdownType] || null : null;
  const cityMetaMap = useMemo(() => buildCityMetaMap(dropdowns), [dropdowns]);
  const visibleDropdownTypes = useMemo(() => {
    const query = String(dropdownTypeQuery || "").trim().toLowerCase();
    if (!query) return dropdownTypesWithReferralSources;
    return dropdownTypesWithReferralSources.filter((item) =>
      [item.label, item.group, item.description, item.type]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [dropdownTypeQuery, dropdownTypesWithReferralSources]);
  const selectedDropdownItems = useMemo(() => {
    const items = selectedDropdownMeta?.type === REFERRAL_SOURCE_DROPDOWN_TYPE
      ? referralSources
      : safeArray(selectedDropdownMeta ? dropdowns[selectedDropdownMeta.type] : []);
    const query = String(dropdownItemQuery || "").trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      [item.name, item.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [dropdownItemQuery, dropdowns, referralSources, selectedDropdownMeta]);
  const cityTierOverview = useMemo(() => {
    const tierItems = safeArray(dropdowns["city-tiers"]);
    const mappedCityIds = Object.values(cityTierAssignments).flatMap((value) => safeNumberArray(value));

    return {
      totalTiers: tierItems.length,
      activeCities: safeArray(dropdowns.cities).filter((item) => item.is_active !== false).length,
      mappedCities: mappedCityIds.length,
      uniqueMappedCities: new Set(mappedCityIds).size,
    };
  }, [cityTierAssignments, dropdowns]);

  const stats = useMemo(() => {
    const activeUsers = users.filter((user) => user.is_active !== false).length;
    return {
      users: users.length,
      roles: roles.length,
      permissions: permissions.length,
      features: features.length,
      featurePermissions: featurePermissions.length,
      userTypes: userTypes.length,
      referralSources: referralSources.length,
      policies: policies.length,
      dropdownItems: dropdownTypesWithReferralSources.reduce((sum, item) => sum + Number(item.itemCount || 0), 0),
      activeUsers,
    };
  }, [users, roles, permissions, features, featurePermissions, userTypes, referralSources, policies, dropdownTypesWithReferralSources]);

  const selectedRoleRecord = useMemo(
    () => roles.find((role) => Number(role.id) === Number(selectedRoleId)) || null,
    [roles, selectedRoleId]
  );

  const canDeleteSelectedRole =
    Boolean(selectedRoleId) &&
    Boolean(selectedRoleRecord) &&
    !selectedRoleRecord.is_system_role &&
    canManageRoles &&
    !saving &&
    !accessLoading;

  const deleteRoleHelpText = !selectedRoleId
    ? "Select a role to manage its access and delete options."
    : selectedRoleRecord?.is_system_role
      ? "System roles cannot be deleted. Select a custom role if you want to remove it."
      : "Custom roles can be deleted. If the role is still assigned to users, remove those assignments first.";

  const userTypeAssignmentOptions = useMemo(() => {
    const activeOptions = userTypes
      .filter((item) => item.is_active !== false)
      .map((item) => ({
        value: String(item.id),
        label: item.name,
      }));

    const inactiveAssignedOptions = userTypes
      .filter((item) => item.is_active === false)
      .map((item) => ({
        value: String(item.id),
        label: `${item.name} (Inactive)`,
      }));

    return [...activeOptions, ...inactiveAssignedOptions];
  }, [userTypes]);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError("");
      const requests = [
        API.get("/admin/users", authHeaders),
        API.get("/admin/rbac/summary", authHeaders),
        (canManageFeatures || canManagePolicies) ? API.get("/admin/rbac/dropdowns", authHeaders) : Promise.resolve(null),
        canManageReferralSources ? API.get("/admin/rbac/referral-sources", authHeaders) : Promise.resolve(null),
        canManagePolicies ? API.get("/admin/rbac/policies", authHeaders) : Promise.resolve(null),
        canAssignProfiles ? API.get("/admin/profile-assignments/users", authHeaders) : Promise.resolve(null),
      ];
      const [usersRes, summaryRes, dropdownRes, referralSourcesRes, policiesRes, assignmentUsersRes] = await Promise.allSettled(requests);

      const nextUsers =
        usersRes.status === "fulfilled" && Array.isArray(usersRes.value?.data)
          ? usersRes.value.data
          : [];
      const summary =
        summaryRes.status === "fulfilled" && summaryRes.value?.data
          ? summaryRes.value.data
          : {};
      const nextRoles = Array.isArray(summary.roles) ? summary.roles : [];
      const nextPermissions = Array.isArray(summary.permissions) ? summary.permissions : [];
      const nextFeatures = Array.isArray(summary.features) ? summary.features : [];
      const nextFeaturePermissions = Array.isArray(summary.feature_permissions) ? summary.feature_permissions : [];
      const nextUserTypes = Array.isArray(summary.user_types) ? summary.user_types : [];
      const nextPermissionSchema =
        summary.permission_schema && typeof summary.permission_schema === "object"
          ? summary.permission_schema
          : initialPermissionSchema;

      setUsers(nextUsers);
      setRoles(nextRoles);
      setPermissions(nextPermissions);
      setFeatures(nextFeatures);
      setFeaturePermissions(nextFeaturePermissions);
      setUserTypes(nextUserTypes);
      setReferralSources(
        referralSourcesRes.status === "fulfilled" && Array.isArray(referralSourcesRes.value?.data)
          ? referralSourcesRes.value.data
          : []
      );
      setPolicies(
        (
          policiesRes.status === "fulfilled" && Array.isArray(policiesRes.value?.data)
            ? policiesRes.value.data
            : Array.isArray(summary.access_policies)
              ? summary.access_policies
              : []
        ).map(normalizePolicyForEditor)
      );
      setPermissionSchema(nextPermissionSchema);
      const dropdownPayload =
        dropdownRes.status === "fulfilled" &&
        dropdownRes.value?.data &&
        typeof dropdownRes.value.data === "object"
          ? normalizeDropdownPayload(dropdownRes.value.data)
          : { items: initialDropdowns, types: initialDropdownTypes };
      setDropdowns(dropdownPayload.items);
      setDropdownTypes(dropdownPayload.types);
      setCityTierAssignments(
        dropdownRes.status === "fulfilled" &&
        dropdownRes.value?.data &&
        typeof dropdownRes.value.data?.city_tier_assignments === "object"
          ? dropdownRes.value.data.city_tier_assignments
          : initialCityTierAssignments
      );
      setAssignableUsers(
        assignmentUsersRes.status === "fulfilled" && Array.isArray(assignmentUsersRes.value?.data)
          ? assignmentUsersRes.value.data
          : []
      );

      const loadErrors = [];
      if (usersRes.status === "rejected") {
        loadErrors.push(usersRes.reason?.response?.data?.error || "users");
      }
      if (summaryRes.status === "rejected") {
        loadErrors.push(summaryRes.reason?.response?.data?.error || "rbac summary");
      }
      if (dropdownRes.status === "rejected") {
        loadErrors.push(dropdownRes.reason?.response?.data?.error || "dropdown masters");
      }
      if (referralSourcesRes.status === "rejected") {
        loadErrors.push(referralSourcesRes.reason?.response?.data?.error || "referral sources");
      }
      if (policiesRes.status === "rejected") {
        loadErrors.push(policiesRes.reason?.response?.data?.error || "access policies");
      }
      if (assignmentUsersRes.status === "rejected") {
        loadErrors.push(assignmentUsersRes.reason?.response?.data?.error || "assignment users");
      }

      if (loadErrors.length) {
        setError(`Some admin data could not load: ${loadErrors.join(", ")}`);
      }

      const preferredRoleId =
        nextRoles.find((role) => !role.is_system_role)?.id ||
        nextRoles[0]?.id ||
        null;

      if (!selectedRoleId && preferredRoleId) {
        setSelectedRoleId(Number(preferredRoleId));
      } else if (selectedRoleId && !nextRoles.some((role) => Number(role.id) === Number(selectedRoleId))) {
        setSelectedRoleId(preferredRoleId ? Number(preferredRoleId) : null);
      }
      const nextAssignableUsers =
        assignmentUsersRes.status === "fulfilled" && Array.isArray(assignmentUsersRes.value?.data)
          ? assignmentUsersRes.value.data
          : [];
      if (!selectedAssigneeId && nextAssignableUsers[0]?.id) {
        setSelectedAssigneeId(String(nextAssignableUsers[0].id));
      } else if (
        selectedAssigneeId &&
        !nextAssignableUsers.some((assignmentUser) => String(assignmentUser.id) === String(selectedAssigneeId))
      ) {
        setSelectedAssigneeId(nextAssignableUsers[0]?.id ? String(nextAssignableUsers[0].id) : "");
      }
      if (!selectedFeatureId && nextFeatures[0]?.id) {
        setSelectedFeatureId(Number(nextFeatures[0].id));
      } else if (selectedFeatureId && !nextFeatures.some((feature) => Number(feature.id) === Number(selectedFeatureId))) {
        setSelectedFeatureId(nextFeatures[0]?.id ? Number(nextFeatures[0].id) : null);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to load RBAC data.");
    } finally {
      setLoading(false);
    }
  };

  const loadProfileVerification = async (overrides = {}) => {
    if (!canViewProfileVerification) return;

    const nextStatus = overrides.status ?? profileVerificationStatus;
    const nextQuery = overrides.q ?? profileVerificationQuery;

    try {
      setProfileVerificationLoading(true);
      setError("");
      const response = await API.get("/admin/rbac/profile-verification", {
        ...authHeaders,
        params: {
          status: nextStatus,
          q: nextQuery || undefined,
        },
      });

      setProfileVerificationItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setProfileVerificationStats(
        response.data?.stats && typeof response.data.stats === "object"
          ? response.data.stats
          : initialProfileVerificationStats
      );
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load profile verification list.");
    } finally {
      setProfileVerificationLoading(false);
    }
  };

  const openVerificationProfile = (profileId) => {
    if (!profileId) return;
    profileVerificationScrollTopRef.current = profileVerificationListRef.current?.scrollTop || 0;
    setSelectedVerificationProfileId(String(profileId));
  };

  const closeVerificationProfile = () => {
    setSelectedVerificationProfileId(null);
    window.requestAnimationFrame(() => {
      if (profileVerificationListRef.current) {
        profileVerificationListRef.current.scrollTop = profileVerificationScrollTopRef.current || 0;
      }
    });
  };

  const loadDiagnostics = async (userId) => {
    const targetId = Number(userId || selectedDiagnosticUserId);
    if (!targetId) {
      setError("Select a user to run diagnostics.");
      return;
    }

    try {
      setDiagnosticsLoading(true);
      setError("");
      const res = await API.get(`/admin/rbac/diagnostics/users/${targetId}`, authHeaders);
      setDiagnosticsData(res.data || null);
    } catch (err) {
      setDiagnosticsData(null);
      setError(err.response?.data?.error || "Failed to load diagnostics.");
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  const loadRoleAccess = async (roleId) => {
    if (!roleId) return;
    try {
      setAccessLoading(true);
      setError("");
      const res = await API.get(`/admin/rbac/roles/${roleId}/access`, authHeaders);
      const access = res.data || {};
      setSelectedRoleMeta({
        name: access.role?.name || "",
        description: access.role?.description || "",
        parent_role_id: access.role?.parent_role_id != null ? String(access.role.parent_role_id) : "",
      });
      setDraftFeatureIds((access.features || []).map((item) => Number(item.id)));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load role access.");
    } finally {
      setAccessLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedDiagnosticUserId && users.length) {
      setSelectedDiagnosticUserId(String(users[0].id));
    }
  }, [selectedDiagnosticUserId, users]);

  useEffect(() => {
    if (!selectedAssigneeId) {
      setAssignedProfiles([]);
      setAssignmentResults([]);
      return;
    }
    loadAssignedProfiles(selectedAssigneeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssigneeId]);

  useEffect(() => {
    if (visibleTabs.some((item) => item.id === tab)) return;
    setTab(visibleTabs[0]?.id || "users");
  }, [tab, visibleTabs]);

  useEffect(() => {
    if (tab !== "profile_verification" || !canViewProfileVerification) return;
    loadProfileVerification();
  }, [tab, canViewProfileVerification]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === "profile_verification") return;
    setSelectedVerificationProfileId(null);
  }, [tab]);

  useEffect(() => {
    if (selectedRoleId) loadRoleAccess(selectedRoleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoleId]);

  useEffect(() => {
    const selected = userTypes.find((item) => Number(item.id) === Number(selectedUserTypeId));
    if (!selected) {
      setSelectedUserTypeMeta(initialUserType);
      return;
    }
    setSelectedUserTypeMeta({
      code: selected.code || "",
      name: selected.name || "",
      description: selected.description || "",
    });
  }, [selectedUserTypeId, userTypes]);

  useEffect(() => {
    if (!selectedDropdownType) return;
    if (dropdownTypeMap[selectedDropdownType]) return;
    setSelectedDropdownType("");
    setDropdownItemQuery("");
    setNewDropdown(initialDropdownForm);
  }, [dropdownTypeMap, selectedDropdownType]);

  const notifySuccess = (message) => {
    setSuccess(message);
    window.dispatchEvent(
      new CustomEvent("auth-refresh-request", {
        detail: { source: "user-management", message },
      })
    );
    setTimeout(() => setSuccess(""), 2400);
  };

  const handleAddUser = async () => {
    const email = newUser.email.trim().toLowerCase();
    const password = newUser.password;
    const roleIds = safeArray(newUser.role_ids).map(Number).filter(Boolean);

    if (!email || !password || roleIds.length === 0) {
      setError("Email, password and at least one role are required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await API.post(
        "/admin/users",
        {
          name: newUser.name.trim() || email.split("@")[0],
          email,
          password,
          role_ids: roleIds,
          role_id: roleIds[0],
          user_type_id: newUser.user_type_id || null,
        },
        authHeaders
      );
      setNewUser(initialUser);
      setShowAddUser(false);
      notifySuccess("User created.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add user.");
    } finally {
      setSaving(false);
    }
  };

  const updateRoles = async (userId, roleIds) => {
    const finalRoleIds = safeArray(roleIds).map(Number).filter(Boolean);
    if (!finalRoleIds.length) {
      setError("User must have at least one role.");
      return;
    }

    try {
      setError("");
      await API.put(
        `/admin/users/${userId}/role`,
        { role_ids: finalRoleIds, role_id: finalRoleIds[0] },
        authHeaders
      );
      notifySuccess("User roles updated.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update user roles.");
    }
  };

  const updateUserType = async (userId, userTypeId) => {
    try {
      setUserTypeSavingId(Number(userId));
      setError("");
      await API.put(
        `/admin/users/${userId}/user-type`,
        { user_type_id: userTypeId || null },
        authHeaders
      );
      notifySuccess("User type updated.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update user type.");
    } finally {
      setUserTypeSavingId(null);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Delete this user? The account will be disabled.")) return;
    try {
      setError("");
      await API.delete(`/admin/users/${userId}`, authHeaders);
      notifySuccess("User deleted.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete user.");
    }
  };

  const loadAssignedProfiles = async (userId) => {
    if (!userId) {
      setAssignedProfiles([]);
      return;
    }

    try {
      setAssignmentLoading(true);
      setError("");
      const res = await API.get(`/admin/profile-assignments?user_id=${userId}`, authHeaders);
      setAssignedProfiles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setAssignedProfiles([]);
      setError(err.response?.data?.error || "Failed to load assigned profiles.");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const runProfileAssignmentSearch = async () => {
    const query = String(assignmentSearch.q || "").trim();
    if (!query) {
      setError("Enter a profile number or name to search.");
      return;
    }

    try {
      setAssignmentLoading(true);
      setError("");
      const res = await API.get("/profiles/search", {
        ...authHeaders,
        params: {
          q: query,
          search_by: assignmentSearch.search_by || "profile_no",
          limit: 25,
          source: "user_task_assignment",
        },
      });
      setAssignmentResults(Array.isArray(res.data) ? res.data : safeArray(res.data?.items));
    } catch (err) {
      setAssignmentResults([]);
      setError(err.response?.data?.error || "Failed to search profiles for assignment.");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const assignProfileToUser = async (profileId) => {
    if (!selectedAssigneeId || !profileId) {
      setError("Select a target user before assigning profiles.");
      return;
    }

    try {
      setAssignmentBusyProfileId(Number(profileId));
      setError("");
      await API.post(
        "/admin/profile-assignments",
        {
          assigned_user_id: Number(selectedAssigneeId),
          profile_ids: [profileId],
        },
        authHeaders
      );
      notifySuccess("Profile assigned.");
      await loadAssignedProfiles(selectedAssigneeId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to assign profile.");
    } finally {
      setAssignmentBusyProfileId(null);
    }
  };

  const unassignProfileFromUser = async (profileId) => {
    if (!selectedAssigneeId || !profileId) return;
    if (!window.confirm("Remove this assigned profile?")) return;

    try {
      setAssignmentBusyProfileId(Number(profileId));
      setError("");
      await API.delete("/admin/profile-assignments", {
        ...authHeaders,
        data: {
          assigned_user_id: Number(selectedAssigneeId),
          profile_id: profileId,
        },
      });
      notifySuccess("Profile unassigned.");
      await loadAssignedProfiles(selectedAssigneeId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to unassign profile.");
    } finally {
      setAssignmentBusyProfileId(null);
    }
  };

  const createRole = async () => {
    if (!newRole.name.trim()) {
      setError("Role name is required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await API.post("/admin/rbac/roles", newRole, authHeaders);
      setNewRole(initialRole);
      notifySuccess("Role created.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create role.");
    } finally {
      setSaving(false);
    }
  };

  const saveSelectedRole = async () => {
    if (!selectedRoleId) return;
    const normalizedParentRoleId =
      selectedRoleMeta.parent_role_id === undefined || selectedRoleMeta.parent_role_id === null || selectedRoleMeta.parent_role_id === ""
        ? null
        : Number(selectedRoleMeta.parent_role_id);

    if (selectedRoleCurrentParentId && normalizedParentRoleId !== selectedRoleCurrentParentId) {
      if (normalizedParentRoleId === null || selectedRoleUpperParentIds.includes(normalizedParentRoleId)) {
        setError("You cannot move this role above its current parent hierarchy.");
        return;
      }
    }

    try {
      setSaving(true);
      setError("");
      await API.put(`/admin/rbac/roles/${selectedRoleId}`, selectedRoleMeta, authHeaders);
      notifySuccess("Role updated.");
      await loadAll();
      await loadRoleAccess(selectedRoleId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update role.");
    } finally {
      setSaving(false);
    }
  };

  const cloneSelectedRole = async () => {
    if (!selectedRoleId) return;
    const defaultName = `${selectedRoleMeta.name || "Role"} Copy`;
    const clonedName = window.prompt("Enter name for cloned role", defaultName);
    if (!clonedName?.trim()) return;

    try {
      setSaving(true);
      setError("");
      await API.post(
        `/admin/rbac/roles/${selectedRoleId}/clone`,
        {
          name: clonedName.trim(),
          description: selectedRoleMeta.description || null,
        },
        authHeaders
      );
      notifySuccess("Role cloned.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to clone role.");
    } finally {
      setSaving(false);
    }
  };

  const deleteSelectedRole = async () => {
    if (!selectedRoleId) return;
    if (!window.confirm("Delete this role?")) return;
    try {
      setSaving(true);
      setError("");
      await API.delete(`/admin/rbac/roles/${selectedRoleId}`, authHeaders);
      notifySuccess("Role deleted.");
      setSelectedRoleId(null);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete role.");
    } finally {
      setSaving(false);
    }
  };

  const saveRoleAccess = async () => {
    if (!selectedRoleId) return;
    try {
      setSaving(true);
      setError("");
      await API.put(
        `/admin/rbac/roles/${selectedRoleId}/access`,
        {
          feature_ids: draftFeatureIds,
        },
        authHeaders
      );
      notifySuccess("Role access updated.");
      await loadRoleAccess(selectedRoleId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update role access.");
    } finally {
      setSaving(false);
    }
  };

  const createPermission = async () => {
    if (!newPermission.module.trim() || !newPermission.action.trim()) {
      setError("Permission module and action are required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await API.post("/admin/rbac/permissions", newPermission, authHeaders);
      setNewPermission(initialPermission);
      notifySuccess("Permission created.");
      await loadAll();
    } catch (err) {
      if (err.response?.status === 409) {
        await loadAll();
      }
      setError(err.response?.data?.error || "Failed to create permission.");
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionModuleChange = (moduleName) => {
    setNewPermission({
      ...newPermission,
      module: moduleName,
      action: "",
      field_key: "",
    });
  };

  const handlePermissionActionChange = (action) => {
    setNewPermission({
      ...newPermission,
      action,
      field_key: "",
    });
  };

  const handlePermissionCatalogModuleChange = (moduleName) => {
    setPermissionCatalogFilters({
      module: moduleName,
      action: "",
      field_key: "",
    });
  };

  const handlePermissionCatalogActionChange = (action) => {
    setPermissionCatalogFilters((current) => ({
      ...current,
      action,
      field_key: "",
    }));
  };

  const deletePermission = async (permissionId) => {
    if (!window.confirm("Delete this permission?")) return;
    try {
      setError("");
      await API.delete(`/admin/rbac/permissions/${permissionId}`, authHeaders);
      notifySuccess("Permission deleted.");
      await loadAll();
      if (selectedRoleId) await loadRoleAccess(selectedRoleId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete permission.");
    }
  };

  const createFeature = async () => {
    if (!newFeature.feature_key.trim() || !newFeature.module.trim() || !newFeature.label.trim()) {
      setError("Feature key, module and label are required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await API.post("/admin/rbac/features", newFeature, authHeaders);
      setNewFeature(initialFeature);
      notifySuccess("Feature created.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create feature.");
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureModuleChange = (moduleName) => {
    setNewFeature({
      ...newFeature,
      module: moduleName,
    });
  };

  const saveFeaturePermissions = async () => {
    if (!selectedFeatureId) return;
    try {
      setSaving(true);
      setError("");
      await API.put(
        `/admin/rbac/features/${selectedFeatureId}/permissions`,
        { permission_ids: draftFeaturePermissionIds },
        authHeaders
      );
      notifySuccess("Feature permissions updated.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update feature permissions.");
    } finally {
      setSaving(false);
    }
  };

  const deleteFeature = async (featureId) => {
    if (!window.confirm("Delete this feature?")) return;
    try {
      setError("");
      await API.delete(`/admin/rbac/features/${featureId}`, authHeaders);
      notifySuccess("Feature deleted.");
      await loadAll();
      if (selectedRoleId) await loadRoleAccess(selectedRoleId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete feature.");
    }
  };

  const createUserType = async () => {
    if (!newUserType.code.trim() || !newUserType.name.trim()) {
      setError("User type code and name are required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await API.post("/admin/rbac/user-types", newUserType, authHeaders);
      setNewUserType(initialUserType);
      notifySuccess("User type created.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create user type.");
    } finally {
      setSaving(false);
    }
  };

  const createReferralSource = async () => {
    if (!newReferralSource.name.trim()) {
      setError("Referral source name is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await API.post(
        "/admin/rbac/referral-sources",
        {
          name: newReferralSource.name.trim(),
          description: newReferralSource.description.trim(),
          sort_order: newReferralSource.sort_order,
          is_trusted: newReferralSource.is_trusted === true,
          is_active: newReferralSource.is_active !== false,
        },
        authHeaders
      );
      setNewReferralSource(initialReferralSource);
      notifySuccess("Referral source created.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create referral source.");
    } finally {
      setSaving(false);
    }
  };

  const updateReferralSourceDraft = (sourceId, field, value) => {
    setReferralSources((prev) =>
      prev.map((item) =>
        Number(item.id) === Number(sourceId)
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  };

  const saveReferralSource = async (source) => {
    if (!source?.name?.trim()) {
      setError("Referral source name is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await API.put(
        `/admin/rbac/referral-sources/${source.id}`,
        {
          name: source.name.trim(),
          description: String(source.description || "").trim(),
          sort_order: source.sort_order,
          is_trusted: source.is_trusted === true,
          is_active: source.is_active !== false,
        },
        authHeaders
      );
      notifySuccess("Referral source updated.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update referral source.");
    } finally {
      setSaving(false);
    }
  };

  const deleteReferralSource = async (source) => {
    if (!source?.id) return;
    if (!window.confirm(`Delete referral source "${source.name}"? This cannot be undone.`)) return;

    try {
      setSaving(true);
      setError("");
      await API.delete(`/admin/rbac/referral-sources/${source.id}`, authHeaders);
      notifySuccess("Referral source deleted.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete referral source.");
    } finally {
      setSaving(false);
    }
  };

  const updateProfileVerification = async (profileId, nextVerified) => {
    if (!profileId || !canVerifyProfiles) return;

    try {
      setProfileVerificationBusyId(Number(profileId));
      setError("");
      await API.patch(
        `/profiles/${profileId}/verification`,
        { is_verified: nextVerified === true },
        authHeaders
      );
      notifySuccess(nextVerified ? "Profile verified." : "Profile verification removed.");
      await loadProfileVerification();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update profile verification.");
    } finally {
      setProfileVerificationBusyId(null);
    }
  };

  const deleteVerificationProfile = async (item) => {
    if (!item?.id || !canDeleteProfiles) return;
    const label = [item.profile_no ? `#${getProfileNumberDisplay(item)}` : "", getVerificationProfileName(item)].filter(Boolean).join(" ");
    const confirmed = window.confirm(
      `Delete profile ${label || item.id}? This will permanently delete the profile and all related details. This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setProfileDeleteBusyId(Number(item.id));
      setError("");
      await API.delete(`/profiles/${item.id}`, authHeaders);
      notifySuccess("Profile deleted.");
      await loadProfileVerification({ status: profileVerificationStatus, q: profileVerificationQuery });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete profile.");
    } finally {
      setProfileDeleteBusyId(null);
    }
  };

  const createPolicy = async () => {
    if (!newPolicy.policy_name.trim()) {
      setError("Policy name is required.");
      return;
    }
    if (!newPolicy.subject_type || !newPolicy.subject_id) {
      setError("Subject type and subject are required.");
      return;
    }
    if (!newPolicy.module || !newPolicy.action) {
      setError("Module and action are required.");
      return;
    }

    let conditionJson;
    try {
      conditionJson = getPolicyConditionPayload(newPolicy);
    } catch (err) {
      setError(err.message || "Condition JSON must be valid JSON.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await API.post("/admin/rbac/policies", { ...newPolicy, condition_json: conditionJson }, authHeaders);
      setNewPolicy(createInitialPolicyDraft());
      notifySuccess("Access policy created.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create access policy.");
    } finally {
      setSaving(false);
    }
  };

  const resetNewPolicyForm = () => {
    setError("");
    setNewPolicy(createInitialPolicyDraft());
  };

  const savePolicy = async (policy) => {
    if (!policy?.policy_name?.trim()) {
      setError("Policy name is required.");
      return;
    }
    if (!policy.subject_type || !policy.subject_id) {
      setError("Subject type and subject are required.");
      return;
    }
    if (!policy.module || !policy.action) {
      setError("Module and action are required.");
      return;
    }

    let conditionJson;
    try {
      conditionJson = getPolicyConditionPayload(policy);
    } catch (err) {
      setError(err.message || "Condition JSON must be valid JSON.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await API.put(
        `/admin/rbac/policies/${policy.id}`,
        {
          policy_name: policy.policy_name,
          subject_type: policy.subject_type,
          subject_id: policy.subject_id,
          module: policy.module,
          action: policy.action,
          effect: policy.effect,
          priority: policy.priority,
          is_active: policy.is_active !== false,
          condition_json: conditionJson,
        },
        authHeaders
      );
      notifySuccess("Access policy updated.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update access policy.");
    } finally {
      setSaving(false);
    }
  };

  const resetPolicyDraft = (policyId) => {
    setError("");
    setPolicies((prev) =>
      prev.map((item) =>
        Number(item.id) === Number(policyId)
          ? normalizePolicyForEditor(item._originalSnapshot || item)
          : item
      )
    );
  };

  const deletePolicy = async (policy) => {
    if (!policy?.id) return;
    if (!window.confirm(`Delete policy "${policy.policy_name || `#${policy.id}`}"? This cannot be undone.`)) return;

    try {
      setSaving(true);
      setError("");
      await API.delete(`/admin/rbac/policies/${policy.id}`, authHeaders);
      notifySuccess("Access policy deleted.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete access policy.");
    } finally {
      setSaving(false);
    }
  };

  const updatePolicyDraft = (policyId, field, value) => {
    setPolicies((prev) =>
      prev.map((item) =>
        Number(item.id) === Number(policyId)
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  };

  const updateNewPolicyConditionField = (field, value) => {
    setNewPolicy((prev) => syncPolicyDraftFromFields({ ...prev, [field]: value }));
  };

  const updateExistingPolicyConditionField = (policyId, field, value) => {
    setPolicies((prev) =>
      prev.map((item) =>
        Number(item.id) === Number(policyId)
          ? syncPolicyDraftFromFields({ ...item, [field]: value })
          : item
      )
    );
  };

  const updateNewPolicyJsonText = (value) => {
    setNewPolicy((prev) => syncPolicyDraftFromJsonText({ ...prev, condition_json_text: value }));
  };

  const updateExistingPolicyJsonText = (policyId, value) => {
    setPolicies((prev) =>
      prev.map((item) =>
        Number(item.id) === Number(policyId)
          ? syncPolicyDraftFromJsonText({ ...item, condition_json_text: value })
          : item
      )
    );
  };

  const createDropdownItem = async () => {
    if (!selectedDropdownMeta) return;
    if (!newDropdown.name.trim()) {
      setError("Dropdown name is required.");
      return;
    }
    if (selectedDropdownMeta.parentField && !newDropdown.parent_id) {
      setError(`Please select ${selectedDropdownMeta.parentLabel || "a parent option"} first.`);
      return;
    }

    try {
      setSaving(true);
      setError("");
      if (selectedDropdownMeta.type === REFERRAL_SOURCE_DROPDOWN_TYPE) {
        await API.post(
          "/admin/rbac/referral-sources",
          buildDropdownRequestBody(selectedDropdownMeta, newDropdown),
          authHeaders
        );
        setNewDropdown(initialReferralSource);
        notifySuccess("Referral source created.");
      } else {
        await API.post(
          `/admin/rbac/dropdowns/${selectedDropdownMeta.type}`,
          buildDropdownRequestBody(selectedDropdownMeta, newDropdown),
          authHeaders
        );
        setNewDropdown(initialDropdownForm);
        notifySuccess("Dropdown item created.");
      }
      await loadAll();
    } catch (err) {
      setError(
        err.response?.data?.error ||
        (selectedDropdownMeta.type === REFERRAL_SOURCE_DROPDOWN_TYPE
          ? "Failed to create referral source."
          : "Failed to create dropdown item.")
      );
    } finally {
      setSaving(false);
    }
  };

  const updateDropdownDraft = (type, id, field, value) => {
    if (type === REFERRAL_SOURCE_DROPDOWN_TYPE) {
      setReferralSources((prev) =>
        prev.map((item) =>
          Number(item.id) === Number(id) ? { ...item, [field]: value } : item
        )
      );
      return;
    }

    setDropdowns((prev) => ({
      ...prev,
      [type]: safeArray(prev[type]).map((item) =>
        Number(item.id) === Number(id) ? { ...item, [field]: value } : item
      ),
    }));
  };

  const saveDropdownItem = async (type, item) => {
    const meta = dropdownTypeMap[type];
    if (!meta) return;
    if (!item?.name?.trim()) {
      setError("Dropdown name is required.");
      return;
    }
    if (meta.parentField && !item[meta.parentField]) {
      setError(`${meta.label} must have ${meta.parentLabel || "a parent option"}.`);
      return;
    }

    try {
      setSaving(true);
      setError("");
      if (type === REFERRAL_SOURCE_DROPDOWN_TYPE) {
        await API.put(
          `/admin/rbac/referral-sources/${item.id}`,
          buildDropdownRequestBody(meta, item),
          authHeaders
        );
        notifySuccess("Referral source updated.");
      } else {
        await API.put(
          `/admin/rbac/dropdowns/${type}/${item.id}`,
          buildDropdownRequestBody(meta, item),
          authHeaders
        );
        notifySuccess("Dropdown item updated.");
      }
      await loadAll();
    } catch (err) {
      setError(
        err.response?.data?.error ||
        (type === REFERRAL_SOURCE_DROPDOWN_TYPE
          ? "Failed to update referral source."
          : "Failed to update dropdown item.")
      );
    } finally {
      setSaving(false);
    }
  };

  const updateCityTierAssignmentDraft = (tierId, cityIds) => {
    setCityTierAssignments((prev) => ({
      ...prev,
      [tierId]: cityIds,
    }));
  };

  const saveCityTierAssignment = async (tierId) => {
    try {
      setSaving(true);
      setError("");
      await API.put(
        `/admin/rbac/city-tiers/${tierId}/cities`,
        { city_ids: safeNumberArray(cityTierAssignments[tierId]) },
        authHeaders
      );
      notifySuccess("City tier mapping updated.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update city tier mapping.");
    } finally {
      setSaving(false);
    }
  };

  const deactivateDropdownItem = async (type, item) => {
    const meta = dropdownTypeMap[type];
    if (!meta || !meta.supportsActive) return;
    const actionLabel = item.is_active === false ? "reactivate" : "deactivate";
    if (!window.confirm(`Are you sure you want to ${actionLabel} "${item.name}"?`)) return;

    try {
      setSaving(true);
      setError("");
      if (type === REFERRAL_SOURCE_DROPDOWN_TYPE) {
        await API.put(
          `/admin/rbac/referral-sources/${item.id}`,
          {
            ...buildDropdownRequestBody(meta, item),
            is_active: item.is_active === false,
          },
          authHeaders
        );
        notifySuccess(`Referral source ${item.is_active === false ? "re-activated" : "deactivated"}.`);
      } else {
        await API.put(
          `/admin/rbac/dropdowns/${type}/${item.id}`,
          {
            ...buildDropdownRequestBody(meta, item),
            is_active: item.is_active === false,
          },
          authHeaders
        );
        notifySuccess(`Dropdown item ${item.is_active === false ? "re-activated" : "deactivated"}.`);
      }
      await loadAll();
    } catch (err) {
      setError(
        err.response?.data?.error ||
        (type === REFERRAL_SOURCE_DROPDOWN_TYPE
          ? "Failed to update referral source status."
          : "Failed to update dropdown item status.")
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteDropdownItem = async (type, item) => {
    if (!window.confirm(`Delete "${item.name}" permanently? This cannot be undone.`)) return;

    try {
      setSaving(true);
      setError("");
      if (type === REFERRAL_SOURCE_DROPDOWN_TYPE) {
        await API.delete(`/admin/rbac/referral-sources/${item.id}`, authHeaders);
        notifySuccess("Referral source deleted.");
      } else {
        await API.delete(`/admin/rbac/dropdowns/${type}/${item.id}`, authHeaders);
        notifySuccess("Dropdown item deleted.");
      }
      await loadAll();
    } catch (err) {
      setError(
        err.response?.data?.error ||
        (type === REFERRAL_SOURCE_DROPDOWN_TYPE
          ? "Failed to delete referral source."
          : "Failed to delete dropdown item.")
      );
    } finally {
      setSaving(false);
    }
  };

  const openDropdownManager = (type) => {
    const meta = dropdownTypeMap[type];
    if (!meta) return;
    setSelectedDropdownType(type);
    setDropdownItemQuery("");
    setNewDropdown(type === REFERRAL_SOURCE_DROPDOWN_TYPE ? initialReferralSource : initialDropdownForm);
  };

  const closeDropdownManager = () => {
    setSelectedDropdownType("");
    setDropdownItemQuery("");
    setNewDropdown(initialDropdownForm);
  };

  const saveSelectedUserType = async () => {
    if (!selectedUserTypeId) return;
    if (!selectedUserTypeMeta.code.trim() || !selectedUserTypeMeta.name.trim()) {
      setError("User type code and name are required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await API.put(
        `/admin/rbac/user-types/${selectedUserTypeId}`,
        {
          code: selectedUserTypeMeta.code.trim(),
          name: selectedUserTypeMeta.name.trim(),
          description: selectedUserTypeMeta.description.trim(),
        },
        authHeaders
      );
      notifySuccess("User type updated.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update user type.");
    } finally {
      setSaving(false);
    }
  };

  const toggleMultiSelect = (currentValues, setValues, id) => {
    const numericId = Number(id);
    setValues(
      currentValues.includes(numericId)
        ? currentValues.filter((value) => value !== numericId)
        : [...currentValues, numericId]
    );
  };

  useEffect(() => {
    if (!selectedFeatureId) {
      setDraftFeaturePermissionIds([]);
      return;
    }
    setDraftFeaturePermissionIds(
      featurePermissions
        .filter((item) => Number(item.feature_id) === Number(selectedFeatureId))
        .map((item) => Number(item.permission_id))
    );
  }, [selectedFeatureId, featurePermissions]);

  if (loading) {
    return <div style={S.center}>Loading RBAC console...</div>;
  }

  return (
    <div style={S.page}>
      <div style={S.hero}>
        <div>
          <p style={S.eyebrow}>Admin Console</p>
          <h1 style={S.title}>RBAC Administration</h1>
          <p style={S.subtitle}>
            Manage users, roles, permissions, features and user types without developer changes.
          </p>
        </div>
        <button style={S.primaryBtn} onClick={loadAll}>
          Refresh
        </button>
      </div>

      <div style={S.statGrid}>
        <StatCard label="Users" value={stats.users} />
        <StatCard label="Active Users" value={stats.activeUsers} />
        <StatCard label="Roles" value={stats.roles} />
        <StatCard label="Permissions" value={stats.permissions} />
        <StatCard label="Features" value={stats.features} />
        <StatCard label="Feature Maps" value={stats.featurePermissions} />
        <StatCard label="Policies" value={stats.policies} />
        <StatCard label="Dropdowns" value={stats.dropdownItems} />
      </div>

      {error && <div style={S.errorBox}>{error}</div>}
      {success && <div style={S.successBox}>{success}</div>}

      <div style={S.tabs}>
        {visibleTabs.map((item) => (
          <button
            key={item.id}
            type="button"
            style={{ ...S.tabBtn, ...(tab === item.id ? S.tabBtnActive : {}) }}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div style={S.section}>
          <div style={S.sectionHead}>
            <div>
              <h2 style={S.sectionTitle}>User Access</h2>
              <p style={S.sectionSub}>Assign one or more roles and a user type to each user.</p>
            </div>
            <button style={S.primaryBtn} onClick={() => setShowAddUser(true)} disabled={!canCreateUsers}>
              + Add User
            </button>
          </div>

          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>User</th>
                  <th style={S.th}>Roles</th>
                  <th style={S.th}>User Type</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Last Login</th>
                  <th style={S.thRight}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td style={S.td}>
                      <div style={S.userName}>{user.name || user.email}</div>
                      <div style={S.muted}>{user.email}</div>
                    </td>
                    <td style={S.td}>
                      <div style={S.chipWrap}>
                        {roles.map((role) => {
                          const checked = getRoleIds(user).includes(Number(role.id));
                          return (
                            <label
                              key={role.id}
                              style={{
                                ...S.chip,
                                ...(checked ? S.chipActive : {}),
                                ...(!canAssignRoles ? S.chipDisabled : {}),
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  if (!canAssignRoles) return;
                                  const existing = getRoleIds(user);
                                  const next = checked
                                    ? existing.filter((value) => value !== Number(role.id))
                                    : [...existing, Number(role.id)];
                                  updateRoles(user.id, next);
                                }}
                                style={S.hiddenCheckbox}
                                disabled={!canAssignRoles}
                              />
                              {role.name}
                            </label>
                          );
                        })}
                      </div>
                    </td>
                    <td style={S.td}>
                      {canAssignRoles ? (
                        <select
                          style={S.inlineSelect}
                          value={user.user_type_id != null ? String(user.user_type_id) : ""}
                          onChange={(e) => updateUserType(user.id, e.target.value)}
                          disabled={userTypeSavingId === Number(user.id)}
                        >
                          <option value="">No user type</option>
                          {userTypeAssignmentOptions.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        getUserTypeName(user.user_type_id, userTypes)
                      )}
                    </td>
                    <td style={S.td}>{user.is_active === false ? "Inactive" : "Active"}</td>
                    <td style={S.td}>{formatDateTime(user.last_login_at)}</td>
                    <td style={S.tdRight}>
                      <button style={S.deleteBtn} onClick={() => deleteUser(user.id)} disabled={!canDeleteUsers}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "task_assignments" && (
        <div style={S.section}>
          <div style={S.sectionHead}>
            <div>
              <h2 style={S.sectionTitle}>Task Assignment</h2>
              <p style={S.sectionSub}>Assign profiles to users at your level or below. Once a user has active profile assignments, their profile access automatically follows those assignments.</p>
            </div>
          </div>

          <div style={S.dualGrid}>
            <div style={S.card}>
              <h3 style={S.cardTitle}>Target User</h3>
              <Field label="Assignable User">
                <select
                  style={S.input}
                  value={selectedAssigneeId}
                  onChange={(e) => {
                    setSelectedAssigneeId(e.target.value);
                    setAssignmentResults([]);
                  }}
                >
                  <option value="">Select user</option>
                  {assignableUsers.map((assignmentUser) => (
                    <option key={assignmentUser.id} value={assignmentUser.id}>
                      {formatAssignableUserLabel(assignmentUser)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Search By">
                <select
                  style={S.input}
                  value={assignmentSearch.search_by}
                  onChange={(e) => setAssignmentSearch((current) => ({ ...current, search_by: e.target.value }))}
                >
                  <option value="profile_no">Profile No.</option>
                  <option value="name">Name</option>
                </select>
              </Field>

              <Field label="Search Profiles">
                <input
                  style={S.input}
                  value={assignmentSearch.q}
                  onChange={(e) => setAssignmentSearch((current) => ({ ...current, q: e.target.value }))}
                  placeholder={assignmentSearch.search_by === "profile_no" ? "Enter profile no. e.g. M123" : "Enter profile name"}
                />
              </Field>

              <div style={S.actionRow}>
                <button style={S.primaryBtn} onClick={runProfileAssignmentSearch} disabled={assignmentLoading || !selectedAssigneeId}>
                  {assignmentLoading ? "Searching..." : "Search Profiles"}
                </button>
              </div>

              <p style={S.helperText}>
                Assignment is allowed only to users whose effective role sits at your level or below in the hierarchy tree. No extra policy setup is required.
              </p>
            </div>

            <div style={S.card}>
              <h3 style={S.cardTitle}>Assigned Profiles</h3>
              {!selectedAssigneeId ? (
                <div style={S.emptyStateBox}>Select a user to view current profile assignments.</div>
              ) : assignmentLoading && assignedProfiles.length === 0 ? (
                <div style={S.emptyStateBox}>Loading assigned profiles…</div>
              ) : assignedProfiles.length === 0 ? (
                <div style={S.emptyStateBox}>No active profile assignments for this user.</div>
              ) : (
                <div style={S.listWrap}>
                  {assignedProfiles.map((profile) => (
                    <div key={profile.profile_id} style={S.listRow}>
                      <div>
                        <div style={S.rowTitle}>#{getProfileNumberDisplay(profile, "—")} · {profile.full_name || "Unnamed profile"}</div>
                        <div style={S.muted}>{[profile.gender, profile.city, profile.state].filter(Boolean).join(" · ") || "Profile details available after open"}</div>
                      </div>
                      <button
                        style={{ ...S.deleteBtn, ...(assignmentBusyProfileId === Number(profile.profile_id) ? S.btnDisabled : {}) }}
                        onClick={() => unassignProfileFromUser(profile.profile_id)}
                        disabled={assignmentBusyProfileId === Number(profile.profile_id)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ ...S.card, marginTop: 18 }}>
            <h3 style={S.cardTitle}>Search Results</h3>
            {assignmentResults.length === 0 ? (
              <div style={S.emptyStateBox}>Search for profiles to assign them here.</div>
            ) : (
              <div style={S.listWrap}>
                {assignmentResults.map((profile) => {
                  const alreadyAssigned = assignedProfiles.some((item) => String(item.profile_id) === String(profile.id));
                  return (
                    <div key={profile.id} style={S.listRow}>
                      <div>
                        <div style={S.rowTitle}>#{getProfileNumberDisplay(profile, "—")} · {profile.full_name || "Unnamed profile"}</div>
                        <div style={S.muted}>{[profile.gender, profile.city, profile.state].filter(Boolean).join(" · ") || "No summary available"}</div>
                      </div>
                      <button
                        style={{ ...S.primaryBtn, ...(alreadyAssigned || assignmentBusyProfileId === Number(profile.id) ? S.btnDisabled : {}) }}
                        onClick={() => assignProfileToUser(profile.id)}
                        disabled={alreadyAssigned || assignmentBusyProfileId === Number(profile.id)}
                      >
                        {alreadyAssigned ? "Assigned" : "Assign"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "roles" && (
        <div style={S.section}>
          <div style={S.dualGrid}>
            <div style={S.card}>
              <h2 style={S.sectionTitle}>Create Role</h2>
              <Field label="Role Name">
                <input style={S.input} value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} />
              </Field>
              <Field label="Description">
                <textarea style={S.textarea} value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} />
              </Field>
              <Field label="Parent Role">
                <select
                  style={S.input}
                  value={newRole.parent_role_id || ""}
                  onChange={(e) => setNewRole({ ...newRole, parent_role_id: e.target.value })}
                >
                  <option value="">No parent role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {formatRoleHierarchyOption(role)}
                    </option>
                  ))}
                </select>
              </Field>
              <button style={S.primaryBtn} onClick={createRole} disabled={saving || !canManageRoles}>
                Add Role
              </button>
            </div>

            <div style={S.card}>
              <h2 style={S.sectionTitle}>Role Access Matrix</h2>
              <Field label="Select Role">
                <select style={S.input} value={selectedRoleId || ""} onChange={(e) => setSelectedRoleId(Number(e.target.value) || null)}>
                  <option value="">Select role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}{role.is_system_role ? " (System)" : ""}
                    </option>
                  ))}
                </select>
              </Field>

              {selectedRoleId && (
                <>
                  <Field label="Role Name">
                    <input style={S.input} value={selectedRoleMeta.name} onChange={(e) => setSelectedRoleMeta({ ...selectedRoleMeta, name: e.target.value })} />
                  </Field>
                  <Field label="Description">
                    <textarea style={S.textarea} value={selectedRoleMeta.description} onChange={(e) => setSelectedRoleMeta({ ...selectedRoleMeta, description: e.target.value })} />
                  </Field>
                  <Field label="Parent Role">
                    <select
                      style={S.input}
                      value={selectedRoleMeta.parent_role_id || ""}
                      onChange={(e) => setSelectedRoleMeta({ ...selectedRoleMeta, parent_role_id: e.target.value })}
                    >
                      {!selectedRoleCurrentParentId && <option value="">No parent role</option>}
                      {selectedRoleParentOptions.map((role) => (
                        <option key={role.id} value={role.id}>
                          {formatRoleHierarchyOption(role)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {selectedRoleParentGuardMessage && <div style={S.helperText}>{selectedRoleParentGuardMessage}</div>}
                  <div style={S.actionRow}>
                    <button style={S.secondaryBtn} onClick={saveSelectedRole} disabled={saving || accessLoading || !canManageRoles}>
                      Save Role
                    </button>
                    <button style={S.secondaryBtn} onClick={cloneSelectedRole} disabled={saving || accessLoading || !canManageRoles}>
                      Clone Role
                    </button>
                    <button
                      style={{
                        ...S.deleteBtn,
                        ...(!canDeleteSelectedRole ? S.btnDisabled : {}),
                      }}
                      onClick={deleteSelectedRole}
                      disabled={!canDeleteSelectedRole}
                      title={selectedRoleRecord?.is_system_role ? "System roles cannot be deleted" : "Delete selected role"}
                    >
                      Delete Role
                    </button>
                  </div>
                  <div style={S.helperText}>{deleteRoleHelpText}</div>

                  <div style={S.hierarchyPanel}>
                    <div style={S.hierarchyPanelHead}>
                      <div>
                        <h3 style={S.subTitle}>Hierarchy Snapshot</h3>
                        <p style={S.helperText}>
                          Track where this role sits before you save parent-role changes.
                        </p>
                      </div>
                      <div style={S.hierarchyStatRow}>
                        <span style={S.hierarchyStatChip}>Depth {selectedRoleHierarchy?.depth ?? 0}</span>
                        <span style={S.hierarchyStatChip}>
                          {selectedRoleHierarchy?.children.length ?? 0} child{(selectedRoleHierarchy?.children.length ?? 0) === 1 ? "" : "ren"}
                        </span>
                        <span style={S.hierarchyStatChip}>
                          {selectedRoleHierarchy?.descendantCount ?? 0} descendant{(selectedRoleHierarchy?.descendantCount ?? 0) === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>

                    <div style={S.hierarchyPathWrap}>
                      {selectedRolePath.length > 0 ? (
                        selectedRolePath.map((role, index) => (
                          <div key={role.id} style={S.hierarchyPathStep}>
                            <span
                              style={{
                                ...S.hierarchyPathChip,
                                ...(Number(role.id) === Number(selectedRoleId) ? S.hierarchyPathChipActive : {}),
                              }}
                            >
                              {role.name}
                            </span>
                            {index < selectedRolePath.length - 1 && <span style={S.hierarchyPathArrow}>/</span>}
                          </div>
                        ))
                      ) : (
                        <span style={S.muted}>Select a role to inspect its hierarchy path.</span>
                      )}
                    </div>
                  </div>

                  <div style={S.accessGrid}>
                    <div>
                      <h3 style={S.subTitle}>Features</h3>
                      <p style={S.helperText}>
                        Use these admin-friendly checkboxes to define role access. When you save, backend automatically converts these into permission atoms through feature-to-permission mapping.
                      </p>
                      <div style={S.selectorList}>
                        {features.map((feature) => (
                          <label key={feature.id} style={S.selectorItem}>
                            <input
                              type="checkbox"
                              checked={draftFeatureIds.includes(Number(feature.id))}
                              onChange={() => canManageRoles && toggleMultiSelect(draftFeatureIds, setDraftFeatureIds, feature.id)}
                              disabled={!canManageRoles}
                            />
                            <span>{feature.label} ({feature.feature_key})</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button style={S.primaryBtn} onClick={saveRoleAccess} disabled={saving || accessLoading || !canManageRoles}>
                    Save Role Access
                  </button>
                </>
              )}
            </div>
          </div>

          <div style={{ ...S.card, marginTop: 18 }}>
            <div style={S.hierarchyMatrixHead}>
              <div>
                <h2 style={S.sectionTitle}>{hierarchyViewMode === "tree" ? "Hierarchy Tree" : "Hierarchy Matrix"}</h2>
                <p style={S.sectionSub}>
                  Switch between a dependency matrix and a plain-language tree to explain the same hierarchy in different formats.
                </p>
              </div>
              <div style={S.hierarchyTopControls}>
                <div style={S.hierarchyViewSwitch}>
                  <button
                    type="button"
                    style={{ ...S.hierarchyViewBtn, ...(hierarchyViewMode === "tree" ? S.hierarchyViewBtnActive : {}) }}
                    onClick={() => setHierarchyViewMode("tree")}
                  >
                    Tree View
                  </button>
                  <button
                    type="button"
                    style={{ ...S.hierarchyViewBtn, ...(hierarchyViewMode === "matrix" ? S.hierarchyViewBtnActive : {}) }}
                    onClick={() => setHierarchyViewMode("matrix")}
                  >
                    Matrix View
                  </button>
                </div>
                <div style={S.hierarchyStatRow}>
                  <span style={S.hierarchyStatChip}>{roleHierarchyView.rootRoles.length} root{roleHierarchyView.rootRoles.length === 1 ? "" : "s"}</span>
                  <span style={S.hierarchyStatChip}>{roleHierarchyView.leafCount} leaf{roleHierarchyView.leafCount === 1 ? "" : "s"}</span>
                  <span style={S.hierarchyStatChip}>Max depth {roleHierarchyView.maxDepth}</span>
                </div>
              </div>
            </div>

            {roleHierarchyView.orderedRoles.length === 0 ? (
              <div style={S.emptyStateBox}>No roles are available yet.</div>
            ) : hierarchyViewMode === "tree" ? (
              <div style={S.hierarchyTreeWrap}>
                <div style={S.hierarchyTreeSummary}>
                  Read this like an org chart. Parent roles sit above their child roles, and the connector lines show reporting branches.
                </div>
                <div style={S.hierarchyTreeViewport}>
                  <div style={S.hierarchyForest}>
                    {roleHierarchyView.rootRoles.map((role) => (
                      <RoleHierarchyTreeNode key={role.id} role={role} selectedRoleId={selectedRoleId} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div style={S.hierarchyLegend}>
                  <span style={{ ...S.hierarchyLegendChip, ...S.hierarchyCellSelf }}>Self</span>
                  <span style={{ ...S.hierarchyLegendChip, ...S.hierarchyCellDirect }}>Direct child</span>
                  <span style={{ ...S.hierarchyLegendChip, ...S.hierarchyCellIndirect }}>2+ levels below</span>
                </div>

                <div style={S.hierarchyMatrixWrap}>
                <table style={S.hierarchyMatrixTable}>
                  <thead>
                    <tr>
                      <th style={{ ...S.hierarchyMatrixCorner, ...S.hierarchyMatrixStickyLeft }}>Ancestor \ Descendant</th>
                      {roleHierarchyView.orderedRoles.map((role) => (
                        <th
                          key={`col-${role.id}`}
                          style={{
                            ...S.hierarchyMatrixHeader,
                            ...(Number(role.id) === Number(selectedRoleId) ? S.hierarchyMatrixHeaderActive : {}),
                          }}
                          title={role.name}
                        >
                          <span style={S.hierarchyMatrixHeaderLabel}>{role.name}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {roleHierarchyView.orderedRoles.map((ancestorRole) => {
                      const relationMap = roleHierarchyView.descendantDistanceByAncestorId.get(Number(ancestorRole.id)) || new Map();
                      return (
                        <tr key={`row-${ancestorRole.id}`}>
                          <th
                            style={{
                              ...S.hierarchyMatrixRowHeader,
                              ...S.hierarchyMatrixStickyLeft,
                              ...(Number(ancestorRole.id) === Number(selectedRoleId) ? S.hierarchyMatrixHeaderActive : {}),
                            }}
                            title={ancestorRole.name}
                          >
                            <div style={S.hierarchyRowRole}>
                              <span style={S.hierarchyIndentGuide}>{ancestorRole.depth > 0 ? `${". ".repeat(ancestorRole.depth)}` : ""}</span>
                              <span>{ancestorRole.name}</span>
                            </div>
                          </th>
                          {roleHierarchyView.orderedRoles.map((descendantRole) => {
                            const relationDistance = relationMap.get(Number(descendantRole.id)) || null;
                            const isSelf = Number(ancestorRole.id) === Number(descendantRole.id);
                            const isSelectedAxis =
                              Number(ancestorRole.id) === Number(selectedRoleId) || Number(descendantRole.id) === Number(selectedRoleId);
                            return (
                              <td
                                key={`cell-${ancestorRole.id}-${descendantRole.id}`}
                                style={{
                                  ...S.hierarchyMatrixCell,
                                  ...(isSelf
                                    ? S.hierarchyCellSelf
                                    : relationDistance === 1
                                      ? S.hierarchyCellDirect
                                      : relationDistance
                                        ? S.hierarchyCellIndirect
                                        : S.hierarchyCellEmpty),
                                  ...(isSelectedAxis ? S.hierarchyCellSelectedAxis : {}),
                                }}
                                title={
                                  isSelf
                                    ? `${ancestorRole.name} is the same role`
                                    : relationDistance
                                      ? `${ancestorRole.name} is ${relationDistance} level${relationDistance === 1 ? "" : "s"} above ${descendantRole.name}`
                                      : `${ancestorRole.name} is not an ancestor of ${descendantRole.name}`
                                }
                              >
                                {isSelf ? "Self" : relationDistance ? formatHierarchyMatrixCellLabel(relationDistance) : ""}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "diagnostics" && (
        <div style={S.section}>
          <div style={S.sectionHead}>
            <div>
              <h2 style={S.sectionTitle}>Access Diagnostics</h2>
              <p style={S.sectionSub}>Validate final roles, permissions, policies and runtime capability for a user.</p>
            </div>
          </div>

          <div style={S.dualGrid}>
            <div style={S.card}>
              <Field label="User">
                <select
                  style={S.select}
                  value={selectedDiagnosticUserId}
                  onChange={(e) => {
                    setSelectedDiagnosticUserId(e.target.value);
                    setDiagnosticsData(null);
                  }}
                >
                  <option value="">Select user</option>
                  {users.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.email}
                    </option>
                  ))}
                </select>
              </Field>
              <div style={S.buttonRow}>
                <button style={S.primaryBtn} onClick={() => loadDiagnostics(selectedDiagnosticUserId)} disabled={diagnosticsLoading || !selectedDiagnosticUserId}>
                  {diagnosticsLoading ? "Running..." : "Run Diagnostics"}
                </button>
              </div>
              <p style={S.muted}>
                This preview shows the effective runtime access after role permissions, user type and access policies are merged.
              </p>
            </div>

            <div style={S.card}>
              <h3 style={S.cardTitle}>What this checks</h3>
              <div style={S.muted}>Active user state, assigned roles, effective permissions, policy conflicts, and final capability outcomes for core modules.</div>
            </div>
          </div>

          {diagnosticsData && (
            <div style={S.diagnosticsGrid}>
              <div style={S.card}>
                <h3 style={S.cardTitle}>Identity</h3>
                <div style={S.listRowCompact}><b>User</b><span>{diagnosticsData.user?.email || "—"}</span></div>
                <div style={S.listRowCompact}><b>Status</b><span>{diagnosticsData.user?.is_active ? "Active" : "Inactive"}</span></div>
                <div style={S.listRowCompact}><b>User Type</b><span>{diagnosticsData.user_type?.name || "Not assigned"}</span></div>
                <div style={S.listRowCompact}><b>Roles</b><span>{safeArray(diagnosticsData.roles).map((item) => item.name).join(", ") || "None"}</span></div>
              </div>

              <div style={S.card}>
                <h3 style={S.cardTitle}>Warnings</h3>
                {safeArray(diagnosticsData.warnings).length === 0 ? (
                  <div style={S.okText}>No high-level warnings found.</div>
                ) : (
                  safeArray(diagnosticsData.warnings).map((item, index) => (
                    <div key={index} style={S.warnBoxCompact}>{item}</div>
                  ))
                )}
              </div>

              <div style={S.card}>
                <h3 style={S.cardTitle}>Policy Conflicts</h3>
                {safeArray(diagnosticsData.policy_conflicts).length === 0 ? (
                  <div style={S.okText}>No policy conflicts detected.</div>
                ) : (
                  safeArray(diagnosticsData.policy_conflicts).map((item, index) => (
                    <div key={index} style={item.level === "warning" ? S.warnBoxCompact : S.infoBoxCompact}>
                      <b>{item.key}</b> — {item.message}
                    </div>
                  ))
                )}
              </div>

              <div style={{ ...S.card, gridColumn: "1 / -1" }}>
                <h3 style={S.cardTitle}>Capabilities</h3>
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Module</th>
                        <th style={S.th}>Action</th>
                        <th style={S.th}>Result</th>
                        <th style={S.th}>Policy Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {safeArray(diagnosticsData.capabilities).map((item) => (
                        <tr key={`${item.module}-${item.action}`}>
                          <td style={S.td}>{item.module}</td>
                          <td style={S.td}>{item.action}</td>
                          <td style={S.td}>
                            <span style={{ ...S.badge, ...(item.allowed ? S.badgeActive : S.badgeMuted) }}>
                              {item.allowed ? "Allowed" : "Denied"}
                            </span>
                          </td>
                          <td style={S.td}>
                            {item.policy?.denied ? "Denied by policy" : ""}
                            {Number(item.policy?.max_results) > 0 ? `${item.policy?.denied ? " · " : ""}Max ${item.policy.max_results}` : ""}
                            {safeArray(item.policy?.hidden_fields).length ? `${(item.policy?.denied || Number(item.policy?.max_results) > 0) ? " · " : ""}Hidden: ${item.policy.hidden_fields.join(", ")}` : ""}
                            {!item.policy ? "—" : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ ...S.card, gridColumn: "1 / -1" }}>
                <h3 style={S.cardTitle}>Effective Permissions</h3>
                <div style={S.permissionChipWrap}>
                  {safeArray(diagnosticsData.permissions).map((item) => (
                    <div key={`${item.module}-${item.action}`} style={S.permissionChip}>
                      <b>{item.module} / {item.action}</b>
                      <span>{item.field_keys.length ? item.field_keys.join(", ") : "general"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "permissions" && (
        <div style={S.section}>
          <div style={S.dualGrid}>
            <div style={S.card}>
              <h2 style={S.sectionTitle}>Create Permission</h2>
              <Field label="Module">
                <select
                  style={S.input}
                  value={newPermission.module}
                  onChange={(e) => handlePermissionModuleChange(e.target.value)}
                >
                  <option value="">Select module</option>
                  {permissionModules.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Action">
                <select
                  style={S.input}
                  value={newPermission.action}
                  onChange={(e) => handlePermissionActionChange(e.target.value)}
                  disabled={!newPermission.module}
                >
                  <option value="">Select action</option>
                  {permissionActions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Field Key">
                <select
                  style={S.input}
                  value={newPermission.field_key}
                  onChange={(e) => setNewPermission({ ...newPermission, field_key: e.target.value })}
                  disabled={!newPermission.action || permissionFieldKeys.length === 0}
                >
                  <option value="">
                    {permissionFieldKeys.length ? "No field-level scope" : "Not applicable for this action"}
                  </option>
                  {permissionFieldKeys.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Description">
                <textarea style={S.textarea} value={newPermission.description} onChange={(e) => setNewPermission({ ...newPermission, description: e.target.value })} />
              </Field>
              <p style={S.helperText}>
                Module, action and field key are controlled values so permission atoms stay clean and consistent in the database.
              </p>
              <button style={S.primaryBtn} onClick={createPermission} disabled={saving || !canManagePermissions}>
                Add Permission
              </button>
            </div>

            <div style={S.card}>
              <h2 style={S.sectionTitle}>Permission Catalog</h2>
              <div style={S.catalogFilterGrid}>
                <Field label="Module">
                  <select
                    style={S.input}
                    value={permissionCatalogFilters.module}
                    onChange={(e) => handlePermissionCatalogModuleChange(e.target.value)}
                  >
                    <option value="">All modules</option>
                    {permissionModules.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Action">
                  <select
                    style={S.input}
                    value={permissionCatalogFilters.action}
                    onChange={(e) => handlePermissionCatalogActionChange(e.target.value)}
                    disabled={!permissionCatalogFilters.module}
                  >
                    <option value="">All actions</option>
                    {permissionCatalogActions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Field Key">
                  <select
                    style={S.input}
                    value={permissionCatalogFilters.field_key}
                    onChange={(e) =>
                      setPermissionCatalogFilters((current) => ({ ...current, field_key: e.target.value }))
                    }
                    disabled={!permissionCatalogFilters.action || permissionCatalogFieldKeys.length === 0}
                  >
                    <option value="">
                      {permissionCatalogFieldKeys.length ? "All field keys" : "Not applicable for this action"}
                    </option>
                    {permissionCatalogFieldKeys.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div style={S.catalogFilterActions}>
                <div style={S.muted}>
                  Showing {filteredPermissions.length} of {permissions.length} permissions
                </div>
                <button
                  style={S.secondaryBtn}
                  onClick={() => setPermissionCatalogFilters(initialPermissionCatalogFilters)}
                  disabled={
                    !permissionCatalogFilters.module &&
                    !permissionCatalogFilters.action &&
                    !permissionCatalogFilters.field_key
                  }
                >
                  Clear Filters
                </button>
              </div>
              <div style={S.listWrap}>
                {filteredPermissions.length ? (
                  filteredPermissions.map((permission) => (
                    <div key={permission.id} style={S.listRow}>
                      <div>
                        <div style={S.rowTitle}>{formatPermission(permission)}</div>
                        <div style={S.muted}>{permission.description || "No description"}</div>
                      </div>
                      <button style={S.deleteBtn} onClick={() => deletePermission(permission.id)} disabled={!canManagePermissions}>
                        Remove
                      </button>
                    </div>
                  ))
                ) : (
                  <div style={S.emptyStateBox}>
                    No permissions match the selected filters.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "features" && (
        <div style={S.section}>
          <div style={S.dualGrid}>
            <div style={S.card}>
              <h2 style={S.sectionTitle}>Create Feature</h2>
              <Field label="Feature Key">
                <input
                  style={S.input}
                  value={newFeature.feature_key}
                  onChange={(e) => setNewFeature({ ...newFeature, feature_key: normalizeKeyInput(e.target.value) })}
                />
              </Field>
              <Field label="Module">
                <select
                  style={S.input}
                  value={newFeature.module}
                  onChange={(e) => handleFeatureModuleChange(e.target.value)}
                >
                  <option value="">Select module</option>
                  {permissionModules.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Label">
                <input style={S.input} value={newFeature.label} onChange={(e) => setNewFeature({ ...newFeature, label: e.target.value })} />
              </Field>
              <Field label="Description">
                <textarea style={S.textarea} value={newFeature.description} onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })} />
              </Field>
              <p style={S.helperText}>
                Feature module is also controlled from backend-approved values so feature grouping stays consistent with permission modules.
              </p>
              <button style={S.primaryBtn} onClick={createFeature} disabled={saving || !canManageFeatures}>
                Add Feature
              </button>
            </div>

            <div style={S.card}>
              <h2 style={S.sectionTitle}>Feature Permission Mapping</h2>
              <Field label="Select Feature">
                <select style={S.input} value={selectedFeatureId || ""} onChange={(e) => setSelectedFeatureId(Number(e.target.value) || null)}>
                  <option value="">Select feature</option>
                  {features.map((feature) => (
                    <option key={feature.id} value={feature.id}>
                      {feature.label} ({feature.feature_key})
                    </option>
                  ))}
                </select>
              </Field>

              {selectedFeatureId && (
                <>
                  <div style={S.listWrap}>
                    {permissions.map((permission) => (
                      <label key={permission.id} style={S.selectorItem}>
                        <input
                          type="checkbox"
                          checked={draftFeaturePermissionIds.includes(Number(permission.id))}
                          onChange={() => canManageFeaturePermissions && toggleMultiSelect(draftFeaturePermissionIds, setDraftFeaturePermissionIds, permission.id)}
                          disabled={!canManageFeaturePermissions}
                        />
                        <span>{formatPermission(permission)}</span>
                      </label>
                    ))}
                  </div>

                  <div style={S.actionRow}>
                    <button style={S.primaryBtn} onClick={saveFeaturePermissions} disabled={saving || !canManageFeaturePermissions}>
                      Save Feature Mapping
                    </button>
                    <button style={S.deleteBtn} onClick={() => deleteFeature(selectedFeatureId)} disabled={saving || !canManageFeatures}>
                      Remove Feature
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "dropdowns" && (
        <div style={S.section}>
          <div style={S.sectionHead}>
            <div>
              <h2 style={S.sectionTitle}>Manage Dropdowns</h2>
              <p style={S.sectionSub}>
                Open any lookup or referral-source card to add, edit, deactivate or permanently delete values.
              </p>
            </div>
          </div>

          <div style={S.dropdownToolbar}>
            <input
              style={S.dropdownSearchInput}
              value={dropdownTypeQuery}
              onChange={(e) => setDropdownTypeQuery(e.target.value)}
              placeholder="Search dropdown families..."
            />
            <div style={S.helperText}>
              Deactivate is safer for live data. Delete is permanent and is blocked when records already use that value.
            </div>
          </div>

          <div style={S.dropdownTypeGrid}>
            {visibleDropdownTypes.map((typeMeta) => (
              <button
                key={typeMeta.type}
                type="button"
                style={S.dropdownTypeCard}
                onClick={() => openDropdownManager(typeMeta.type)}
              >
                <div style={S.dropdownTypeCardTop}>
                  <span style={S.dropdownGroupPill}>{typeMeta.group}</span>
                  <span style={S.dropdownCountPill}>{typeMeta.itemCount}</span>
                </div>
                <div style={S.dropdownTypeCardTitle}>{typeMeta.label}</div>
                <div style={S.dropdownTypeCardText}>{typeMeta.description || "Manage lookup items for this family."}</div>
                <div style={S.dropdownTypeCardMeta}>
                  <span>{typeMeta.activeCount} active</span>
                  <span>{typeMeta.parentLabel ? `Parent: ${typeMeta.parentLabel}` : "Standalone"}</span>
                </div>
              </button>
            ))}
          </div>

          {visibleDropdownTypes.length === 0 && (
            <div style={S.emptyDropdownState}>No dropdown families match this search.</div>
          )}

          {selectedDropdownMeta && (
            <div style={S.modalBackdrop} onClick={(e) => e.target === e.currentTarget && closeDropdownManager()}>
              <div style={S.dropdownModal}>
                <div style={S.dropdownModalHead}>
                  <div>
                    <div style={S.dropdownModalEyebrow}>{selectedDropdownMeta.group}</div>
                    <h3 style={S.dropdownModalTitle}>{selectedDropdownMeta.label}</h3>
                    <p style={S.dropdownModalSub}>
                      {selectedDropdownMeta.description || "Manage lookup values for this dropdown family."}
                    </p>
                  </div>
                  <button style={S.secondaryBtn} onClick={closeDropdownManager} disabled={saving}>
                    Close
                  </button>
                </div>

                {selectedDropdownMeta.type === "city-tiers" && (
                  <div style={S.dropdownModalStats}>
                    <div style={S.dropdownModalStatCard}>
                      <div style={S.dropdownModalStatValue}>{cityTierOverview.totalTiers}</div>
                      <div style={S.dropdownModalStatLabel}>Tier groups</div>
                    </div>
                    <div style={S.dropdownModalStatCard}>
                      <div style={S.dropdownModalStatValue}>{cityTierOverview.activeCities}</div>
                      <div style={S.dropdownModalStatLabel}>Cities in master</div>
                    </div>
                    <div style={S.dropdownModalStatCard}>
                      <div style={S.dropdownModalStatValue}>{cityTierOverview.mappedCities}</div>
                      <div style={S.dropdownModalStatLabel}>Total mapped slots</div>
                    </div>
                    <div style={S.dropdownModalStatCard}>
                      <div style={S.dropdownModalStatValue}>{cityTierOverview.uniqueMappedCities}</div>
                      <div style={S.dropdownModalStatLabel}>Unique mapped cities</div>
                    </div>
                  </div>
                )}

                <div style={S.dropdownModalGrid}>
                  <div style={S.card}>
                    <h4 style={S.subTitle}>Add New Item</h4>
                    {selectedDropdownMeta.parentField && (
                      <Field label={selectedDropdownMeta.parentLabel || "Parent"}>
                        <select
                          style={S.input}
                          value={newDropdown.parent_id}
                          onChange={(e) => setNewDropdown({ ...newDropdown, parent_id: e.target.value })}
                        >
                          <option value="">Select {selectedDropdownMeta.parentLabel || "parent"}</option>
                          {getParentDropdownOptions(selectedDropdownMeta, dropdowns).map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                    )}
                    <Field label="Name">
                      <input
                        style={S.input}
                        value={newDropdown.name}
                        onChange={(e) => setNewDropdown({ ...newDropdown, name: e.target.value })}
                      />
                    </Field>
                    {selectedDropdownMeta.supportsDescription && (
                      <Field label="Description">
                        <textarea
                          style={S.textarea}
                          value={newDropdown.description}
                          onChange={(e) => setNewDropdown({ ...newDropdown, description: e.target.value })}
                        />
                      </Field>
                    )}
                    {selectedDropdownMeta.supportsTrusted && (
                      <label style={S.checkboxRow}>
                        <input
                          type="checkbox"
                          checked={newDropdown.is_trusted === true}
                          onChange={(e) => setNewDropdown({ ...newDropdown, is_trusted: e.target.checked })}
                        />
                        Trusted source
                      </label>
                    )}
                    {selectedDropdownMeta.supportsActive && selectedDropdownMeta.type === REFERRAL_SOURCE_DROPDOWN_TYPE && (
                      <label style={S.checkboxRow}>
                        <input
                          type="checkbox"
                          checked={newDropdown.is_active !== false}
                          onChange={(e) => setNewDropdown({ ...newDropdown, is_active: e.target.checked })}
                        />
                        Active
                      </label>
                    )}
                    <button style={S.primaryBtn} onClick={createDropdownItem} disabled={saving || !canManageDropdowns}>
                      Add Item
                    </button>
                  </div>

                  <div style={S.card}>
                    <div style={S.dropdownListHead}>
                      <div>
                        <h4 style={S.subTitle}>Existing Items</h4>
                        <div style={S.muted}>{safeArray(dropdowns[selectedDropdownMeta.type]).length} total items</div>
                      </div>
                      <input
                        style={S.dropdownSearchInput}
                        value={dropdownItemQuery}
                        onChange={(e) => setDropdownItemQuery(e.target.value)}
                        placeholder={`Search ${selectedDropdownMeta.label.toLowerCase()}...`}
                      />
                    </div>

                    <div style={S.dropdownEditorList}>
                      {selectedDropdownItems.length === 0 && (
                        <div style={S.emptyDropdownState}>No items found for this search.</div>
                      )}
                      {selectedDropdownItems.map((item) => (
                        <DropdownItemEditor
                          key={`${selectedDropdownMeta.type}-${item.id}`}
                          meta={selectedDropdownMeta}
                          item={item}
                          dropdowns={dropdowns}
                          cityTierAssignments={cityTierAssignments}
                          cityMetaMap={cityMetaMap}
                          saving={saving}
                          onFieldChange={updateDropdownDraft}
                          onSave={saveDropdownItem}
                          onCityTierAssignmentChange={updateCityTierAssignmentDraft}
                          onSaveCityTierAssignment={saveCityTierAssignment}
                          onToggleActive={deactivateDropdownItem}
                          onDelete={deleteDropdownItem}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "referral_sources" && (
        <div style={S.section}>
          <div style={S.dualGrid}>
            <div style={S.card}>
              <h2 style={S.sectionTitle}>Create Referral Source</h2>
              <Field label="Name">
                <input
                  style={S.input}
                  value={newReferralSource.name}
                  onChange={(e) => setNewReferralSource((prev) => ({ ...prev, name: e.target.value }))}
                />
              </Field>
              <Field label="Description">
                <textarea
                  style={S.textarea}
                  value={newReferralSource.description}
                  onChange={(e) => setNewReferralSource((prev) => ({ ...prev, description: e.target.value }))}
                />
              </Field>
              <label style={S.checkboxRow}>
                <input
                  type="checkbox"
                  checked={newReferralSource.is_trusted === true}
                  onChange={(e) => setNewReferralSource((prev) => ({ ...prev, is_trusted: e.target.checked }))}
                />
                Trusted source
              </label>
              <label style={S.checkboxRow}>
                <input
                  type="checkbox"
                  checked={newReferralSource.is_active !== false}
                  onChange={(e) => setNewReferralSource((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
                Active
              </label>
              <button style={S.primaryBtn} onClick={createReferralSource} disabled={saving || !canManageReferralSources}>
                Add Referral Source
              </button>
            </div>

            <div style={S.card}>
              <h2 style={S.sectionTitle}>Referral Source Catalog</h2>
              <div style={S.listWrap}>
                {referralSources.length === 0 && <div style={S.muted}>No referral sources created yet.</div>}
                {referralSources.map((source) => (
                  <div key={source.id} style={S.policyCard}>
                    <div style={S.dropdownEditorHead}>
                      <div>
                        <div style={S.rowTitle}>{source.name || "Untitled referral source"}</div>
                        <div style={S.muted}>
                          {source.is_trusted ? "Trusted" : "Not trusted"} • #{source.id}
                        </div>
                      </div>
                      <span style={{ ...S.badge, ...(source.is_active === false ? S.badgeMuted : S.badgeActive) }}>
                        {source.is_active === false ? "Inactive" : "Active"}
                      </span>
                    </div>

                    <div style={S.policyGrid}>
                      <Field label="Name">
                        <input
                          style={S.input}
                          value={source.name || ""}
                          onChange={(e) => updateReferralSourceDraft(source.id, "name", e.target.value)}
                        />
                      </Field>
                      <Field label="Description">
                        <textarea
                          style={S.textarea}
                          value={source.description || ""}
                          onChange={(e) => updateReferralSourceDraft(source.id, "description", e.target.value)}
                        />
                      </Field>
                    </div>

                    <label style={S.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={source.is_trusted === true}
                        onChange={(e) => updateReferralSourceDraft(source.id, "is_trusted", e.target.checked)}
                      />
                      Trusted source
                    </label>
                    <label style={S.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={source.is_active !== false}
                        onChange={(e) => updateReferralSourceDraft(source.id, "is_active", e.target.checked)}
                      />
                      Active
                    </label>

                    <div style={S.actionRow}>
                      <button style={S.primaryBtn} onClick={() => saveReferralSource(source)} disabled={saving || !canManageReferralSources}>
                        Save Referral Source
                      </button>
                      <button style={S.deleteBtn} onClick={() => deleteReferralSource(source)} disabled={saving || !canManageReferralSources}>
                        Delete Referral Source
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "profile_verification" && (
        <div style={S.section}>
          {selectedVerificationProfileId ? (
            <div style={S.card}>
              <div style={S.sectionHead}>
                <div>
                  <h2 style={S.sectionTitle}>Profile Details Review</h2>
                  <p style={S.helperText}>
                    Review the full profile and then return to the verification queue to mark it verified if needed.
                  </p>
                </div>
                <button style={S.secondaryBtn} onClick={closeVerificationProfile}>
                  Back to Verification Queue
                </button>
              </div>

              <ProfileDetail
                profileId={selectedVerificationProfileId}
                token={token}
                user={user}
                onBack={closeVerificationProfile}
                backLabel="Back to Verification Queue"
              />
            </div>
          ) : (
            <div style={S.card}>
              <div style={S.dropdownModalHead}>
                <div>
                <h2 style={S.sectionTitle}>Profile Verification Queue</h2>
                <p style={S.helperText}>
                  Review referral-source based verification and manually verify profiles when needed.
                </p>
              </div>
            </div>

            <div style={S.statGrid}>
              <StatCard label="Total Profiles" value={profileVerificationStats.total || 0} />
              <StatCard label="Verified" value={profileVerificationStats.verified || 0} />
              <StatCard label="Unverified" value={profileVerificationStats.unverified || 0} />
            </div>

            <div style={S.filtersCard}>
              <div style={S.filterGroup}>
                <label style={S.filterLabel}>Status</label>
                <select
                  style={S.input}
                  value={profileVerificationStatus}
                  onChange={(e) => setProfileVerificationStatus(e.target.value)}
                >
                  <option value="all">All Profiles</option>
                  <option value="verified">Verified</option>
                  <option value="unverified">Unverified</option>
                </select>
              </div>

              <div style={{ ...S.filterGroup, flex: 1, minWidth: 240 }}>
                <label style={S.filterLabel}>Search</label>
                <input
                  style={S.input}
                  value={profileVerificationQuery}
                  onChange={(e) => setProfileVerificationQuery(e.target.value)}
                  placeholder="Search by profile no or name"
                />
              </div>

              <div style={S.actionRow}>
                <button
                  style={S.primaryBtn}
                  onClick={() => loadProfileVerification({ status: profileVerificationStatus, q: profileVerificationQuery })}
                  disabled={profileVerificationLoading}
                >
                  {profileVerificationLoading ? "Loading..." : "Apply"}
                </button>
                <button
                  style={S.secondaryBtn}
                  onClick={() => {
                    setProfileVerificationStatus("all");
                    setProfileVerificationQuery("");
                    loadProfileVerification({ status: "all", q: "" });
                  }}
                  disabled={profileVerificationLoading}
                >
                  Reset
                </button>
              </div>
            </div>

            <div ref={profileVerificationListRef} style={S.listWrap}>
              {profileVerificationLoading && (
                <div style={S.muted}>Loading verification list...</div>
              )}
              {!profileVerificationLoading && profileVerificationItems.length === 0 && (
                <div style={S.muted}>No profiles found for the selected filters.</div>
              )}
              {!profileVerificationLoading && profileVerificationItems.map((item) => (
                <VerificationProfileCard
                  key={item.id}
                  item={item}
                  canOpenProfile={canOpenVerificationProfile}
                  canVerifyProfiles={canVerifyProfiles}
                  canDeleteProfiles={canDeleteProfiles}
                  isSaving={profileVerificationBusyId === Number(item.id)}
                  isDeleting={profileDeleteBusyId === Number(item.id)}
                  onOpenProfile={() => openVerificationProfile(item.id)}
                  onToggleVerification={() => updateProfileVerification(item.id, item.is_verified !== true)}
                  onDeleteProfile={() => deleteVerificationProfile(item)}
                />
              ))}
            </div>
            </div>
          )}
        </div>
      )}

      {tab === "policies" && (
        <div style={S.section}>
          <div style={S.dualGrid}>
            <div style={S.card}>
              <h2 style={S.sectionTitle}>Create Access Policy</h2>
              <Field label="Policy Name">
                <input
                  style={S.input}
                  value={newPolicy.policy_name}
                  onChange={(e) => setNewPolicy({ ...newPolicy, policy_name: e.target.value })}
                />
              </Field>
              <Field label="Subject Type">
                <select
                  style={S.input}
                  value={newPolicy.subject_type}
                  onChange={(e) => setNewPolicy({ ...newPolicy, subject_type: e.target.value, subject_id: "" })}
                >
                  <option value="role">Role</option>
                  <option value="user_type">User Type</option>
                  <option value="user">User</option>
                </select>
              </Field>
              <Field label="Subject">
                <select
                  style={S.input}
                  value={newPolicy.subject_id}
                  onChange={(e) => setNewPolicy({ ...newPolicy, subject_id: e.target.value })}
                >
                  <option value="">Select subject</option>
                  {getPolicySubjectOptions(newPolicy.subject_type, { roles, userTypes, users }).map((option) => (
                    <option key={`${newPolicy.subject_type}-${option.id}`} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Module">
                <select
                  style={S.input}
                  value={newPolicy.module}
                  onChange={(e) => setNewPolicy((prev) => syncPolicyDraftFromFields({ ...prev, module: e.target.value, action: "" }))}
                >
                  <option value="">Select module</option>
                  {permissionModules.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Action">
                <select
                  style={S.input}
                  value={newPolicy.action}
                  onChange={(e) => setNewPolicy((prev) => syncPolicyDraftFromFields({ ...prev, action: e.target.value }))}
                  disabled={!newPolicy.module}
                >
                  <option value="">Select action</option>
                  {policyActions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <div style={S.grid2Cols}>
                <Field label="Effect">
                  <select
                    style={S.input}
                    value={newPolicy.effect}
                    onChange={(e) => setNewPolicy({ ...newPolicy, effect: e.target.value })}
                  >
                    <option value="restrict">Restrict</option>
                    <option value="allow">Allow</option>
                    <option value="deny">Deny</option>
                  </select>
                </Field>
                <Field label="Priority">
                  <input
                    style={S.input}
                    type="number"
                    value={newPolicy.priority}
                    onChange={(e) => setNewPolicy({ ...newPolicy, priority: e.target.value })}
                  />
                </Field>
              </div>
              <PolicyConditionBuilder
                policy={newPolicy}
                stateOptions={statePolicyOptions}
                cityOptions={cityPolicyOptions}
                onFieldChange={updateNewPolicyConditionField}
                onJsonTextChange={updateNewPolicyJsonText}
                onToggleAdvanced={() => setNewPolicy((prev) => ({ ...prev, show_advanced_json: !prev.show_advanced_json }))}
              />
              <div style={S.actionRow}>
                <button style={S.primaryBtn} onClick={createPolicy} disabled={saving || !canManagePolicies}>
                  Add Policy
                </button>
                <button style={S.secondaryBtn} onClick={resetNewPolicyForm} disabled={saving || !canManagePolicies}>
                  Reset Form
                </button>
              </div>
            </div>

            <div style={S.card}>
              <h2 style={S.sectionTitle}>Existing Policies</h2>
              <div style={S.listWrap}>
                {policies.length === 0 && <div style={S.muted}>No policies created yet.</div>}
                {policies.map((policy) => (
                  <div key={policy.id} style={S.policyCard}>
                    <div style={S.dropdownEditorHead}>
                      <div>
                        <div style={S.rowTitle}>{policy.policy_name}</div>
                        <div style={S.muted}>
                          {formatPolicySubject(policy, { roles, userTypes, users })} • {policy.module} / {policy.action}
                        </div>
                      </div>
                      <span style={{ ...S.badge, ...(policy.is_active === false ? S.badgeMuted : S.badgeActive) }}>
                        {policy.is_active === false ? "Inactive" : "Active"}
                      </span>
                    </div>
                    <div style={S.policyGrid}>
                      <Field label="Policy Name">
                        <input
                          style={S.input}
                          value={policy.policy_name || ""}
                          onChange={(e) => updatePolicyDraft(policy.id, "policy_name", e.target.value)}
                        />
                      </Field>
                      <Field label="Subject Type">
                        <select
                          style={S.input}
                          value={policy.subject_type || "role"}
                          onChange={(e) => {
                            updatePolicyDraft(policy.id, "subject_type", e.target.value);
                            updatePolicyDraft(policy.id, "subject_id", "");
                          }}
                        >
                          <option value="role">Role</option>
                          <option value="user_type">User Type</option>
                          <option value="user">User</option>
                        </select>
                      </Field>
                      <Field label="Subject">
                        <select
                          style={S.input}
                          value={policy.subject_id || ""}
                          onChange={(e) => updatePolicyDraft(policy.id, "subject_id", e.target.value)}
                        >
                          <option value="">Select subject</option>
                          {getPolicySubjectOptions(policy.subject_type, { roles, userTypes, users }).map((option) => (
                            <option key={`${policy.id}-${option.id}`} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Module">
                        <select
                          style={S.input}
                          value={policy.module || ""}
                          onChange={(e) => {
                            setPolicies((prev) =>
                              prev.map((item) =>
                                Number(item.id) === Number(policy.id)
                                  ? syncPolicyDraftFromFields({ ...item, module: e.target.value, action: "" })
                                  : item
                              )
                            );
                          }}
                        >
                          <option value="">Select module</option>
                          {permissionModules.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Action">
                        <select
                          style={S.input}
                          value={policy.action || ""}
                          onChange={(e) => updateExistingPolicyConditionField(policy.id, "action", e.target.value)}
                        >
                          <option value="">Select action</option>
                          {getModuleActions(policy.module, permissionSchema).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Effect">
                        <select
                          style={S.input}
                          value={policy.effect || "restrict"}
                          onChange={(e) => updatePolicyDraft(policy.id, "effect", e.target.value)}
                        >
                          <option value="restrict">Restrict</option>
                          <option value="allow">Allow</option>
                          <option value="deny">Deny</option>
                        </select>
                      </Field>
                      <Field label="Priority">
                        <input
                          style={S.input}
                          type="number"
                          value={policy.priority ?? 100}
                          onChange={(e) => updatePolicyDraft(policy.id, "priority", e.target.value)}
                        />
                      </Field>
                    </div>
                    <PolicyConditionBuilder
                      policy={policy}
                      stateOptions={statePolicyOptions}
                      cityOptions={cityPolicyOptions}
                      onFieldChange={(field, value) => updateExistingPolicyConditionField(policy.id, field, value)}
                      onJsonTextChange={(value) => updateExistingPolicyJsonText(policy.id, value)}
                      onToggleAdvanced={() => updatePolicyDraft(policy.id, "show_advanced_json", !policy.show_advanced_json)}
                    />
                    <label style={S.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={policy.is_active !== false}
                        onChange={(e) => updatePolicyDraft(policy.id, "is_active", e.target.checked)}
                      />
                      Active
                    </label>
                    <div style={S.actionRow}>
                      <button style={S.primaryBtn} onClick={() => savePolicy(policy)} disabled={saving || !canManagePolicies}>
                        Save Policy
                      </button>
                      <button
                        style={S.secondaryBtn}
                        onClick={() => resetPolicyDraft(policy.id)}
                        disabled={saving || !canManagePolicies}
                      >
                        Reset Changes
                      </button>
                      <button style={S.deleteBtn} onClick={() => deletePolicy(policy)} disabled={saving || !canManagePolicies}>
                        Delete Policy
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "user_types" && (
        <div style={S.section}>
          <div style={S.dualGrid}>
            <div style={S.card}>
              <h2 style={S.sectionTitle}>Create User Type</h2>
              <Field label="Code">
                <input
                  style={S.input}
                  value={newUserType.code}
                  onChange={(e) => setNewUserType({ ...newUserType, code: normalizeKeyInput(e.target.value) })}
                />
              </Field>
              <Field label="Name">
                <input style={S.input} value={newUserType.name} onChange={(e) => setNewUserType({ ...newUserType, name: e.target.value })} />
              </Field>
              <Field label="Description">
                <textarea style={S.textarea} value={newUserType.description} onChange={(e) => setNewUserType({ ...newUserType, description: e.target.value })} />
              </Field>
              <button style={S.primaryBtn} onClick={createUserType} disabled={saving || !canManageUserTypes}>
                Add User Type
              </button>
            </div>

            <div style={S.card}>
              <h2 style={S.sectionTitle}>User Type Catalog</h2>
              <Field label="Select User Type">
                <select
                  style={S.input}
                  value={selectedUserTypeId || ""}
                  onChange={(e) => setSelectedUserTypeId(Number(e.target.value) || null)}
                >
                  <option value="">Select user type</option>
                  {userTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.code})
                    </option>
                  ))}
                </select>
              </Field>

              {selectedUserTypeId && (
                <>
                  <Field label="Code">
                    <input
                      style={S.input}
                      value={selectedUserTypeMeta.code}
                      onChange={(e) =>
                        setSelectedUserTypeMeta({
                          ...selectedUserTypeMeta,
                          code: normalizeKeyInput(e.target.value),
                        })
                      }
                    />
                  </Field>
                  <Field label="Name">
                    <input
                      style={S.input}
                      value={selectedUserTypeMeta.name}
                      onChange={(e) =>
                        setSelectedUserTypeMeta({
                          ...selectedUserTypeMeta,
                          name: e.target.value,
                        })
                      }
                    />
                  </Field>
                  <Field label="Description">
                    <textarea
                      style={S.textarea}
                      value={selectedUserTypeMeta.description}
                      onChange={(e) =>
                        setSelectedUserTypeMeta({
                          ...selectedUserTypeMeta,
                          description: e.target.value,
                        })
                      }
                    />
                  </Field>
                  <button style={S.primaryBtn} onClick={saveSelectedUserType} disabled={saving || !canManageUserTypes}>
                    Save User Type
                  </button>
                </>
              )}

              <div style={{ ...S.listWrap, marginTop: 16 }}>
                {userTypes.map((item) => (
                  <div key={item.id} style={S.listRow}>
                    <div>
                      <div style={S.rowTitle}>{item.name} ({item.code})</div>
                      <div style={S.muted}>{item.description || "No description"}</div>
                    </div>
                    <span style={{ ...S.badge, ...(item.is_active === false ? S.badgeMuted : S.badgeActive) }}>
                      {item.is_active === false ? "Inactive" : "Active"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddUser && canCreateUsers && (
        <div style={S.modalBackdrop} onClick={(e) => e.target === e.currentTarget && setShowAddUser(false)}>
          <div style={S.modal}>
            <h3 style={S.sectionTitle}>Add User</h3>
            <Field label="Name">
              <input style={S.input} value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
            </Field>
            <Field label="Email">
              <input style={S.input} value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
            </Field>
            <Field label="Password">
              <input type="password" style={S.input} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
            </Field>
            <Field label="User Type">
              <select style={S.input} value={newUser.user_type_id} onChange={(e) => setNewUser({ ...newUser, user_type_id: e.target.value })}>
                <option value="">Optional</option>
                {userTypes.filter((item) => item.is_active !== false).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Roles">
              <div style={S.chipWrap}>
                {assignableRoleOptions.map((role) => {
                  const selected = newUser.role_ids.includes(Number(role.id));
                  return (
                    <button
                      key={role.id}
                      type="button"
                      style={{ ...S.chipButton, ...(selected ? S.chipActive : {}) }}
                      onClick={() => {
                        const next = selected
                          ? newUser.role_ids.filter((value) => value !== Number(role.id))
                          : [...newUser.role_ids, Number(role.id)];
                        setNewUser({ ...newUser, role_ids: next });
                      }}
                    >
                      {role.name}
                    </button>
                  );
                })}
              </div>
              {assignableRoleOptions.length === 0 && (
              <div style={S.helperText}>No roles are available.</div>
              )}
            </Field>
            <div style={S.actionRow}>
              <button style={S.secondaryBtn} onClick={() => setShowAddUser(false)} disabled={saving}>
                Cancel
              </button>
              <button style={S.primaryBtn} onClick={handleAddUser} disabled={saving}>
                Create User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={S.statCard}>
      <div style={S.statValue}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

function getVerificationProfileName(item) {
  return [item.first_name, item.middle_name, item.last_name].filter(Boolean).join(" ") || "Unnamed Profile";
}

function getVerificationProfileAge(birthDate) {
  if (!birthDate) return "";
  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  const monthDelta = now.getMonth() - date.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < date.getDate())) age -= 1;
  return age > 0 ? `${age} yrs` : "";
}

function getVerificationInitials(item) {
  return `${item.first_name?.[0] || ""}${item.last_name?.[0] || item.middle_name?.[0] || ""}`.toUpperCase() || "?";
}

function getVerificationUserLabel(email, userId) {
  const localPart = String(email || "").split("@")[0].trim();
  if (localPart) {
    return localPart
      .replace(/[._-]+/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }
  return userId ? `User #${userId}` : "-";
}

function VerificationProfileCard({
  item,
  canOpenProfile,
  canVerifyProfiles,
  canDeleteProfiles,
  isSaving,
  isDeleting,
  onOpenProfile,
  onToggleVerification,
  onDeleteProfile,
}) {
  const [imgError, setImgError] = useState(false);
  const fullName = getVerificationProfileName(item);
  const age = getVerificationProfileAge(item.birth_date);
  const photo = item.primary_photo_url_cache || null;
  const location = item.location_display_cache || [item.city, item.state].filter(Boolean).join(", ");
  const occupation = item.current_occupation_details || item.occupation_type || "";

  return (
    <div style={S.verificationCard}>
      <div style={S.verificationCardHead}>
        <div style={S.verificationIdentityRow}>
          <div style={S.verificationPhotoWrap}>
            {photo && !imgError ? (
              <img src={photo} alt={fullName} style={S.verificationPhoto} onError={() => setImgError(true)} />
            ) : (
              <div style={S.verificationAvatar}>
                <span style={S.verificationAvatarText}>{getVerificationInitials(item)}</span>
              </div>
            )}
          </div>

          <div style={S.verificationIdentityBody}>
            <div style={S.rowTitle}>#{getProfileNumberDisplay(item, "-")} {fullName}</div>
            <div style={S.verificationMetaLine}>
              {[age, item.gender, item.marital_status, item.status || "active"].filter(Boolean).join(" • ")}
            </div>
            {location && <div style={S.verificationMetaLine}>{location}</div>}
            <div style={S.verificationPillRow}>
              <span style={{ ...S.badge, ...(item.is_verified ? S.badgeActive : S.badgeMuted) }}>
                {item.is_verified ? "Verified" : "Unverified"}
              </span>
              {item.referral_source_name && <span style={S.verificationInfoPill}>Referral: {item.referral_source_name}</span>}
              {item.education_summary && <span style={S.verificationInfoPill}>{item.education_summary}</span>}
              {occupation && <span style={S.verificationInfoPill}>{occupation}</span>}
            </div>
          </div>
        </div>
      </div>

      <div style={S.verificationDetailsGrid}>
        <div style={S.verificationInfoCard}>
          <div style={S.verificationInfoLabel}>Referral Source</div>
          <div style={S.verificationInfoValue}>{item.referral_source_name || "-"}</div>
        </div>
        <div style={S.verificationInfoCard}>
          <div style={S.verificationInfoLabel}>Verified At</div>
          <div style={S.verificationInfoValue}>{formatDateTime(item.verified_at)}</div>
        </div>
        <div style={S.verificationInfoCard}>
          <div style={S.verificationInfoLabel}>Verified By</div>
          <div style={S.verificationInfoValue}>{getVerificationUserLabel(item.verified_by_email, item.verified_by)}</div>
        </div>
        <div style={S.verificationInfoCard}>
          <div style={S.verificationInfoLabel}>Created By</div>
          <div style={S.verificationInfoValue}>{getVerificationUserLabel(item.created_by_email, item.created_by)}</div>
        </div>
        <div style={S.verificationInfoCard}>
          <div style={S.verificationInfoLabel}>Profile Status</div>
          <div style={S.verificationInfoValue}>{item.status || "active"}</div>
        </div>
      </div>

      <div style={S.actionRow}>
        {canOpenProfile && (
          <button style={S.secondaryBtn} onClick={onOpenProfile}>
            Open Profile Details
          </button>
        )}
        {canVerifyProfiles && (
          <button style={item.is_verified ? S.secondaryBtn : S.primaryBtn} onClick={onToggleVerification} disabled={isSaving || isDeleting}>
            {isSaving ? "Saving..." : item.is_verified ? "Remove Verification" : "Mark Verified"}
          </button>
        )}
        {canDeleteProfiles && (
          <button style={S.deleteBtn} onClick={onDeleteProfile} disabled={isSaving || isDeleting}>
            {isDeleting ? "Deleting..." : "Delete Profile"}
          </button>
        )}
      </div>
    </div>
  );
}

function DropdownItemEditor({
  meta,
  item,
  dropdowns,
  cityTierAssignments,
  cityMetaMap,
  saving,
  onFieldChange,
  onSave,
  onCityTierAssignmentChange,
  onSaveCityTierAssignment,
  onToggleActive,
  onDelete,
}) {
  const parentOptions = getParentDropdownOptions(meta, dropdowns);
  const selectedCityIds = safeNumberArray(cityTierAssignments?.[item.id]);

  return (
    <div style={S.dropdownEditorCard}>
      <div style={S.dropdownEditorHead}>
        <div>
          <div style={S.rowTitle}>{item.name || "Untitled item"}</div>
          <div style={S.muted}>#{item.id}</div>
        </div>
        <span style={{ ...S.badge, ...(item.is_active === false ? S.badgeMuted : S.badgeActive) }}>
          {item.is_active === false ? "Inactive" : "Active"}
        </span>
      </div>

      <div style={S.dropdownEditorFields}>
        {meta.parentField && (
          <Field label={meta.parentLabel || "Parent"}>
            <select
              style={S.input}
              value={item[meta.parentField] || ""}
              onChange={(e) => onFieldChange(meta.type, item.id, meta.parentField, e.target.value)}
            >
              <option value="">Select {meta.parentLabel || "parent"}</option>
              {parentOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Name">
          <input
            style={S.input}
            value={item.name || ""}
            onChange={(e) => onFieldChange(meta.type, item.id, "name", e.target.value)}
            placeholder="Name"
          />
        </Field>

        {meta.supportsDescription && (
          <Field label="Description">
            <textarea
              style={S.textarea}
              value={item.description || ""}
              onChange={(e) => onFieldChange(meta.type, item.id, "description", e.target.value)}
              placeholder="Description"
            />
          </Field>
        )}

        {meta.supportsTrusted && (
          <label style={S.checkboxRow}>
            <input
              type="checkbox"
              checked={item.is_trusted === true}
              onChange={(e) => onFieldChange(meta.type, item.id, "is_trusted", e.target.checked)}
            />
            Trusted source
          </label>
        )}

        {meta.type === "city-tiers" && (
          <CityTierAssignmentEditor
            tierId={item.id}
            selectedCityIds={selectedCityIds}
            cityMetaMap={cityMetaMap}
            dropdowns={dropdowns}
            saving={saving}
            onChange={onCityTierAssignmentChange}
          />
        )}
      </div>

      <div style={S.actionRow}>
        <button style={S.secondaryBtn} onClick={() => onSave(meta.type, item)} disabled={saving}>
          Save
        </button>
        {meta.type === "city-tiers" && (
          <button style={S.secondaryBtn} onClick={() => onSaveCityTierAssignment(item.id)} disabled={saving}>
            Save Cities
          </button>
        )}
        {meta.supportsActive && (
          <button style={S.secondaryBtn} onClick={() => onToggleActive(meta.type, item)} disabled={saving}>
            {item.is_active === false ? "Activate" : "Deactivate"}
          </button>
        )}
        <button style={S.deleteBtn} onClick={() => onDelete(meta.type, item)} disabled={saving}>
          Delete
        </button>
      </div>
    </div>
  );
}

function CityTierAssignmentEditor({ tierId, selectedCityIds, cityMetaMap, dropdowns, saving, onChange }) {
  const [query, setQuery] = useState("");
  const selectedCityIdSet = useMemo(
    () => new Set(safeNumberArray(selectedCityIds).map((value) => Number(value))),
    [selectedCityIds]
  );
  const activeCityRows = useMemo(
    () => safeArray(dropdowns?.cities).filter((city) => city.is_active !== false),
    [dropdowns]
  );
  const availableCities = useMemo(() => {
    const normalizedQuery = normalizeSearchValue(query);

    return activeCityRows
      .map((city) => cityMetaMap[city.id] || buildFallbackCityMeta(city))
      .filter((city) => !selectedCityIdSet.has(Number(city.id)))
      .filter((city) => !normalizedQuery || city.searchText.includes(normalizedQuery))
      .sort((left, right) => {
        const nameCompare = left.name.localeCompare(right.name);
        if (nameCompare !== 0) return nameCompare;
        return left.locationLabel.localeCompare(right.locationLabel);
      });
  }, [activeCityRows, cityMetaMap, query, selectedCityIdSet]);
  const selectedCities = useMemo(
    () =>
      safeNumberArray(selectedCityIds).map((cityId) => {
        const mappedCity = cityMetaMap[cityId];
        return mappedCity || {
          id: cityId,
          name: `City #${cityId}`,
          locationLabel: "No longer available in the city master",
          searchText: String(cityId),
        };
      }),
    [cityMetaMap, selectedCityIds]
  );

  const updateCities = (cityIds) => {
    onChange(tierId, uniqueNumberArray(cityIds));
  };

  const addCity = (cityId) => {
    updateCities([...selectedCityIds, cityId]);
  };

  const removeCity = (cityId) => {
    updateCities(selectedCityIds.filter((value) => Number(value) !== Number(cityId)));
  };

  return (
    <div style={S.cityTierManager}>
      <div style={S.cityTierManagerHead}>
        <div>
          <div style={S.rowTitle}>Mapped Cities</div>
          <div style={S.muted}>Add cities from the existing city master and remove any city you do not want in this tier.</div>
        </div>
        <div style={S.cityTierCountBadge}>{selectedCities.length} selected</div>
      </div>

      <div style={S.cityTierPanelGrid}>
        <div style={S.cityTierPanel}>
          <div style={S.cityTierPanelHead}>
            <div>
              <div style={S.cityTierPanelTitle}>Available Cities</div>
              <div style={S.muted}>Search the existing city table</div>
            </div>
            <div style={S.cityTierCountBadge}>{availableCities.length}</div>
          </div>
          <input
            style={S.dropdownSearchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search city, tehsil, district or state..."
          />
          <div style={S.cityTierList}>
            {availableCities.length === 0 && (
              <div style={S.cityTierEmptyState}>No cities match this search.</div>
            )}
            {availableCities.map((city) => (
              <div key={`${tierId}-available-${city.id}`} style={S.cityTierListRow}>
                <div style={S.cityTierListCopy}>
                  <div style={S.rowTitle}>{city.name}</div>
                  <div style={S.cityTierLocation}>{city.locationLabel || "Location details unavailable"}</div>
                </div>
                <button
                  type="button"
                  style={S.secondaryBtn}
                  onClick={() => addCity(city.id)}
                  disabled={saving}
                >
                  Add
                </button>
              </div>
            ))}
          </div>
        </div>

        <div style={S.cityTierPanel}>
          <div style={S.cityTierPanelHead}>
            <div>
              <div style={S.cityTierPanelTitle}>Selected Cities</div>
              <div style={S.muted}>Remove any city from this tier before saving</div>
            </div>
            {selectedCities.length > 0 && (
              <button
                type="button"
                style={S.ghostBtn}
                onClick={() => updateCities([])}
                disabled={saving}
              >
                Clear all
              </button>
            )}
          </div>
          <div style={S.cityTierList}>
            {selectedCities.length === 0 && (
              <div style={S.cityTierEmptyState}>No cities are mapped to this tier yet.</div>
            )}
            {selectedCities.map((city) => (
              <div key={`${tierId}-selected-${city.id}`} style={S.cityTierSelectedRow}>
                <div style={S.cityTierListCopy}>
                  <div style={S.rowTitle}>{city.name}</div>
                  <div style={S.cityTierLocation}>{city.locationLabel || "Location details unavailable"}</div>
                </div>
                <button
                  type="button"
                  style={S.deleteBtn}
                  onClick={() => removeCity(city.id)}
                  disabled={saving}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PolicyConditionBuilder({
  policy,
  stateOptions,
  cityOptions,
  onFieldChange,
  onJsonTextChange,
  onToggleAdvanced,
}) {
  const config = getPolicyBuilderConfig(policy?.module, policy?.action);

  return (
    <div style={S.policyBuilder}>
      <div style={S.policyBuilderHead}>
        <div style={S.rowTitle}>Conditions</div>
        <button type="button" style={S.ghostBtn} onClick={onToggleAdvanced}>
          {policy?.show_advanced_json ? "Hide Advanced JSON" : "Show Advanced JSON"}
        </button>
      </div>

      {!config.supported && (
        <div style={S.helperText}>
          This module/action does not have a guided builder yet. Use Advanced JSON for custom conditions.
        </div>
      )}

      {config.supportsMaxResults && (
        <Field label="Max Results">
          <input
            style={S.input}
            type="number"
            min="1"
            value={policy?.max_results ?? ""}
            onChange={(e) => onFieldChange("max_results", e.target.value)}
            placeholder="Example: 50"
          />
        </Field>
      )}

      {config.supportsStates && (
        <Field label="Allowed States">
          <select
            multiple
            size={Math.min(Math.max(stateOptions.length, 4), 8)}
            style={S.multiValueSelect}
            value={safeArray(policy?.allowed_state_ids).map(String)}
            onChange={(e) => onFieldChange("allowed_state_ids", Array.from(e.target.selectedOptions).map((option) => Number(option.value)))}
          >
            {stateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={S.helperText}>Use Ctrl/Cmd to select multiple states.</div>
        </Field>
      )}

      {config.supportsCities && (
        <Field label="Allowed Cities">
          <select
            multiple
            size={Math.min(Math.max(cityOptions.length, 4), 8)}
            style={S.multiValueSelect}
            value={safeArray(policy?.allowed_city_ids).map(String)}
            onChange={(e) => onFieldChange("allowed_city_ids", Array.from(e.target.selectedOptions).map((option) => Number(option.value)))}
          >
            {cityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={S.helperText}>Use Ctrl/Cmd to select multiple cities.</div>
        </Field>
      )}

      {config.supportsAssignedToSelf && (
        <label style={S.checkboxRow}>
          <input
            type="checkbox"
            checked={policy?.assigned_to_self === true}
            onChange={(e) => onFieldChange("assigned_to_self", e.target.checked)}
          />
          Assigned / created by self only
        </label>
      )}

      {config.supportsHiddenFields && (
        <div style={S.field}>
          <span style={S.label}>Hidden Fields</span>
          <div style={S.checkboxGrid}>
            {config.hiddenFieldOptions.map((option) => (
              <label key={option.value} style={S.checkboxRowCompact}>
                <input
                  type="checkbox"
                  checked={safeArray(policy?.hidden_fields).includes(option.value)}
                  onChange={(e) => {
                    const current = new Set(safeArray(policy?.hidden_fields));
                    if (e.target.checked) current.add(option.value);
                    else current.delete(option.value);
                    onFieldChange("hidden_fields", Array.from(current));
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      )}

      {policy?.show_advanced_json && (
        <Field label="Advanced JSON">
          <textarea
            style={{ ...S.textarea, minHeight: 150, fontFamily: "Consolas, monospace" }}
            value={policy?.condition_json_text || "{}"}
            onChange={(e) => onJsonTextChange(e.target.value)}
          />
        </Field>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={S.field}>
      <span style={S.label}>{label}</span>
      {children}
    </label>
  );
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeNumberArray(value) {
  return safeArray(value).map((item) => Number(item)).filter(Number.isFinite);
}

function uniqueNumberArray(value) {
  return Array.from(new Set(safeNumberArray(value)));
}

function normalizeDropdownPayload(payload) {
  const types = safeArray(payload?.types).map((item) => ({
    ...item,
    type: String(item?.type || ""),
  })).filter((item) => item.type);

  const items = {};
  types.forEach((item) => {
    items[item.type] = safeArray(payload?.items?.[item.type] ?? payload?.[item.type]);
  });

  return { items, types };
}

function buildDropdownTypeMap(types) {
  return safeArray(types).reduce((acc, item) => {
    acc[item.type] = item;
    return acc;
  }, {});
}

function buildDropdownRequestBody(meta, item) {
  const body = {
    name: String(item?.name || "").trim(),
  };

  if (meta?.parentField) {
    body[meta.parentField] = item?.[meta.parentField] || item?.parent_id || null;
  }
  if (meta?.supportsDescription) {
    body.description = String(item?.description || "").trim() || null;
  }
  if (meta?.supportsSortOrder) {
    body.sort_order = item?.sort_order === "" || item?.sort_order === undefined ? null : item.sort_order;
  }
  if (meta?.supportsActive) {
    body.is_active = item?.is_active !== false;
  }
  if (meta?.supportsTrusted) {
    body.is_trusted = item?.is_trusted === true;
  }

  return body;
}

function buildCityMetaMap(dropdowns) {
  const tehsilById = buildIdMap(dropdowns?.tehsils);
  const districtById = buildIdMap(dropdowns?.districts);
  const stateById = buildIdMap(dropdowns?.states);
  const countryById = buildIdMap(dropdowns?.countries);

  return safeArray(dropdowns?.cities).reduce((acc, city) => {
    const tehsil = tehsilById[city.tehsil_id];
    const district = tehsil ? districtById[tehsil.district_id] : null;
    const state = district ? stateById[district.state_id] : null;
    const country = state ? countryById[state.country_id] : null;
    const locationParts = [tehsil?.name, district?.name, state?.name, country?.name].filter(Boolean);
    const locationLabel = locationParts.join(" / ");

    acc[city.id] = {
      id: Number(city.id),
      name: city.name || `City #${city.id}`,
      locationLabel,
      searchText: normalizeSearchValue([city.name, ...locationParts].filter(Boolean).join(" ")),
    };
    return acc;
  }, {});
}

function buildIdMap(items) {
  return safeArray(items).reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
}

function buildFallbackCityMeta(city) {
  return {
    id: Number(city?.id),
    name: city?.name || `City #${city?.id}`,
    locationLabel: "",
    searchText: normalizeSearchValue(city?.name || String(city?.id || "")),
  };
}

function getParentDropdownOptions(meta, dropdowns) {
  if (!meta?.parentType) return [];
  return safeArray(dropdowns?.[meta.parentType]).filter((item) => item.is_active !== false);
}

function buildRoleHierarchyView(roleRows = []) {
  const normalizedRoles = safeArray(roleRows)
    .map((role) => ({
      ...role,
      id: Number(role.id),
      name: String(role?.name || `Role #${role?.id}`).trim(),
      parent_role_id:
        role?.parent_role_id === undefined || role?.parent_role_id === null || role?.parent_role_id === ""
          ? null
          : Number(role.parent_role_id),
    }))
    .filter((role) => role.id);

  const sortRolesByName = (left, right) => {
    const byName = String(left.name || "").localeCompare(String(right.name || ""));
    if (byName !== 0) return byName;
    return Number(left.id) - Number(right.id);
  };

  const roleMetaById = new Map(
    normalizedRoles.map((role) => [
      Number(role.id),
      {
        ...role,
        children: [],
        parent: null,
        depth: 0,
        descendantCount: 0,
      },
    ])
  );

  roleMetaById.forEach((role) => {
    const parentId = Number(role.parent_role_id) || null;
    if (!parentId || parentId === Number(role.id)) return;
    const parentRole = roleMetaById.get(parentId);
    if (!parentRole) return;
    role.parent = parentRole;
    parentRole.children.push(role);
  });

  roleMetaById.forEach((role) => {
    role.children.sort(sortRolesByName);
  });

  const rootRoles = Array.from(roleMetaById.values())
    .filter((role) => !role.parent)
    .sort(sortRolesByName);

  const orderedRoles = [];
  const visited = new Set();

  const visitRole = (role, depth) => {
    if (!role || visited.has(role.id)) return;
    visited.add(role.id);
    role.depth = depth;
    orderedRoles.push(role);
    role.children.forEach((child) => visitRole(child, depth + 1));
  };

  rootRoles.forEach((role) => visitRole(role, 0));

  Array.from(roleMetaById.values())
    .sort(sortRolesByName)
    .forEach((role) => {
      if (!visited.has(role.id)) visitRole(role, 0);
    });

  const descendantDistanceByAncestorId = new Map();

  orderedRoles.forEach((ancestorRole) => {
    const distances = new Map();
    const queue = ancestorRole.children.map((child) => ({ role: child, distance: 1 }));
    const seen = new Set([ancestorRole.id]);

    while (queue.length) {
      const current = queue.shift();
      if (!current?.role || seen.has(current.role.id)) continue;
      seen.add(current.role.id);
      distances.set(current.role.id, current.distance);
      current.role.children.forEach((child) => {
        queue.push({ role: child, distance: current.distance + 1 });
      });
    }

    ancestorRole.descendantCount = distances.size;
    descendantDistanceByAncestorId.set(ancestorRole.id, distances);
  });

  const maxDepth = orderedRoles.reduce((max, role) => Math.max(max, Number(role.depth) || 0), 0);
  const leafCount = orderedRoles.filter((role) => role.children.length === 0).length;

  const getPathToRole = (roleId) => {
    const path = [];
    const seen = new Set();
    let currentRole = roleMetaById.get(Number(roleId)) || null;

    while (currentRole && !seen.has(currentRole.id)) {
      path.unshift(currentRole);
      seen.add(currentRole.id);
      currentRole = currentRole.parent || null;
    }

    return path;
  };

  const getAncestorRoleIds = (roleId) => {
    const ancestors = [];
    const visited = new Set();
    let currentRole = roleMetaById.get(Number(roleId)) || null;

    while (currentRole?.parent && !visited.has(currentRole.parent.id)) {
      ancestors.push(Number(currentRole.parent.id));
      visited.add(currentRole.parent.id);
      currentRole = currentRole.parent;
    }

    return ancestors;
  };

  const getDescendantRoleIds = (roleId) =>
    Array.from(descendantDistanceByAncestorId.get(Number(roleId))?.keys() || []);

  return {
    descendantDistanceByAncestorId,
    getAncestorRoleIds,
    getDescendantRoleIds,
    getPathToRole,
    leafCount,
    maxDepth,
    orderedRoles,
    roleMetaById,
    rootRoles,
  };
}

function getRoleIds(user) {
  return safeArray(user.roles).map((role) => Number(role.id)).filter(Boolean);
}

function getUserTypeName(userTypeId, userTypes) {
  const match = userTypes.find((item) => Number(item.id) === Number(userTypeId));
  return match?.name || "-";
}

function formatRoleHierarchyOption(role) {
  const parentName = String(role?.parent_role_name || "").trim();
  return parentName ? `${role.name} (under ${parentName})` : role?.name || "Role";
}

function formatAssignableUserLabel(user) {
  const displayName = String(user?.display_name || "").trim();
  const roleName = String(user?.effective_role_name || "").trim() || "No role";
  const email = String(user?.email || "").trim();
  return [displayName, roleName, email].filter(Boolean).join(" - ");
}

function formatHierarchyMatrixCellLabel(distance) {
  if (!distance) return "";
  if (distance === 1) return "Direct";
  return `${distance} below`;
}

function RoleHierarchyTreeNode({ role, selectedRoleId }) {
  const isSelected = Number(role?.id) === Number(selectedRoleId);
  const children = safeArray(role?.children);
  const hasChildren = children.length > 0;

  return (
    <div style={S.orgTreeNode}>
      <div style={{ ...S.orgTreeCard, ...(isSelected ? S.orgTreeCardActive : {}) }}>
        <div style={S.orgTreeCardTop}>
          <div style={S.orgTreeRoleName}>{role?.name || "Role"}</div>
          <div style={S.orgTreeDepthChip}>Level {Number(role?.depth || 0)}</div>
        </div>
        <div style={S.orgTreeMeta}>
          {children.length || 0} child{children.length === 1 ? "" : "ren"} and {role?.descendantCount || 0} total descendant{(role?.descendantCount || 0) === 1 ? "" : "s"}
        </div>
      </div>

      {hasChildren && (
        <div style={S.orgTreeChildrenWrap}>
          <div style={S.orgTreeStem} />
          <div style={S.orgTreeChildrenRow}>
            {children.map((child, index) => {
              const isOnly = children.length === 1;
              const isFirst = index === 0;
              const isLast = index === children.length - 1;

              return (
                <div key={child.id} style={S.orgTreeBranch}>
                  <div style={S.orgTreeBranchHead}>
                    {!isOnly && (
                      <div
                        style={{
                          ...S.orgTreeBranchLink,
                          ...(isFirst ? S.orgTreeBranchLinkFirst : {}),
                          ...(isLast ? S.orgTreeBranchLinkLast : {}),
                        }}
                      />
                    )}
                    <div style={S.orgTreeBranchStem} />
                  </div>
                  <RoleHierarchyTreeNode role={child} selectedRoleId={selectedRoleId} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function getPolicySubjectOptions(subjectType, { roles, userTypes, users }) {
  if (subjectType === "role") {
    return safeArray(roles).map((item) => ({ id: item.id, label: item.name }));
  }
  if (subjectType === "user_type") {
    return safeArray(userTypes).map((item) => ({ id: item.id, label: `${item.name} (${item.code})` }));
  }
  return safeArray(users).map((item) => ({ id: item.id, label: item.email }));
}

function getPolicyBuilderConfig(moduleName, action) {
  const hiddenFieldOptions = [
    { value: "phone", label: "Phone" },
    { value: "email", label: "Email" },
    { value: "address_line1", label: "Address" },
    { value: "payment_amount", label: "Payment Amount" },
  ];

  if (moduleName === "profiles" && action === "search") {
    return {
      supported: true,
      supportsMaxResults: true,
      supportsStates: true,
      supportsCities: true,
      supportsAssignedToSelf: true,
      supportsAssignedProfilesOnly: false,
      supportsHiddenFields: false,
      hiddenFieldOptions,
    };
  }

  if (moduleName === "profiles" && action === "view") {
    return {
      supported: true,
      supportsMaxResults: false,
      supportsStates: false,
      supportsCities: false,
      supportsAssignedToSelf: false,
      supportsAssignedProfilesOnly: false,
      supportsHiddenFields: true,
      hiddenFieldOptions,
    };
  }

  if (moduleName === "profiles" && action === "edit") {
    return {
      supported: true,
      supportsMaxResults: false,
      supportsStates: false,
      supportsCities: false,
      supportsAssignedToSelf: false,
      supportsAssignedProfilesOnly: false,
      supportsHiddenFields: true,
      hiddenFieldOptions,
    };
  }

  if ((moduleName === "duplicates" || moduleName === "audit") && action === "view") {
    return {
      supported: true,
      supportsMaxResults: true,
      supportsStates: false,
      supportsCities: false,
      supportsAssignedToSelf: false,
      supportsAssignedProfilesOnly: false,
      supportsHiddenFields: false,
      hiddenFieldOptions,
    };
  }

  return {
    supported: false,
    supportsMaxResults: false,
    supportsStates: false,
    supportsCities: false,
    supportsAssignedToSelf: false,
    supportsAssignedProfilesOnly: false,
    supportsHiddenFields: false,
    hiddenFieldOptions,
  };
}

function buildPolicyConditionJson(policy) {
  const next = {};
  const config = getPolicyBuilderConfig(policy?.module, policy?.action);
  const maxResults = Number(policy?.max_results);
  if (config.supportsMaxResults && Number.isFinite(maxResults) && maxResults > 0) next.max_results = maxResults;

  const allowedStateIds = uniqueNumberArray(policy?.allowed_state_ids);
  if (config.supportsStates && allowedStateIds.length) next.allowed_state_ids = allowedStateIds;

  const allowedCityIds = uniqueNumberArray(policy?.allowed_city_ids);
  if (config.supportsCities && allowedCityIds.length) next.allowed_city_ids = allowedCityIds;

  if (config.supportsAssignedToSelf && policy?.assigned_to_self === true) next.assigned_to_self = true;

  const hiddenFields = safeArray(policy?.hidden_fields).map((item) => String(item || "").trim()).filter(Boolean);
  if (config.supportsHiddenFields && hiddenFields.length) next.hidden_fields = Array.from(new Set(hiddenFields));

  return next;
}

function snapshotPolicyForReset(policy) {
  const source = policy && typeof policy === "object" ? policy : initialPolicy;
  const { _originalSnapshot, ...rest } = source;
  return {
    ...rest,
    allowed_state_ids: [...safeArray(rest.allowed_state_ids)],
    allowed_city_ids: [...safeArray(rest.allowed_city_ids)],
    hidden_fields: safeArray(rest.hidden_fields).map((item) => String(item || "").trim()).filter(Boolean),
    condition_json:
      rest.condition_json && typeof rest.condition_json === "object" && !Array.isArray(rest.condition_json)
        ? JSON.parse(JSON.stringify(rest.condition_json))
        : rest.condition_json,
  };
}

function createInitialPolicyDraft() {
  return normalizePolicyForEditor(snapshotPolicyForReset(initialPolicy));
}

function syncPolicyDraftFromFields(policy) {
  const conditionJson = buildPolicyConditionJson(policy);
  return {
    ...policy,
    condition_json_text: JSON.stringify(conditionJson, null, 2),
  };
}

function normalizePolicyForEditor(policy) {
  const originalSnapshot = snapshotPolicyForReset(policy?._originalSnapshot || policy);
  const conditionJson =
    policy?.condition_json && typeof policy.condition_json === "object" && !Array.isArray(policy.condition_json)
      ? policy.condition_json
      : (() => {
          try {
            return JSON.parse(policy?.condition_json_text || "{}");
          } catch (err) {
            return {};
          }
        })();

  return {
    ...policy,
    max_results: conditionJson.max_results ?? "",
    allowed_state_ids: uniqueNumberArray(conditionJson.allowed_state_ids),
    allowed_city_ids: uniqueNumberArray(conditionJson.allowed_city_ids),
    assigned_to_self: conditionJson.assigned_to_self === true,
    assigned_profiles_only: conditionJson.assigned_profiles_only === true,
    hidden_fields: safeArray(conditionJson.hidden_fields).map((item) => String(item || "").trim()).filter(Boolean),
    condition_json: conditionJson,
    condition_json_text: policy?.condition_json_text || JSON.stringify(conditionJson, null, 2),
    show_advanced_json: policy?.show_advanced_json === true,
    _originalSnapshot: originalSnapshot,
  };
}

function syncPolicyDraftFromJsonText(policy) {
  try {
    return normalizePolicyForEditor({
      ...policy,
      condition_json: JSON.parse(policy?.condition_json_text || "{}"),
      show_advanced_json: policy?.show_advanced_json === true,
    });
  } catch (err) {
    return {
      ...policy,
      condition_json_text: policy?.condition_json_text || "{}",
    };
  }
}

function getPolicyConditionPayload(policy) {
  if (policy?.show_advanced_json) {
    try {
      return JSON.parse(policy.condition_json_text || "{}");
    } catch (err) {
      throw new Error("Advanced JSON must be valid JSON.");
    }
  }
  return buildPolicyConditionJson(policy);
}

function formatPolicySubject(policy, { roles, userTypes, users }) {
  const options = getPolicySubjectOptions(policy.subject_type, { roles, userTypes, users });
  const match = options.find((item) => Number(item.id) === Number(policy.subject_id));
  return match ? `${formatOptionLabel(policy.subject_type)}: ${match.label}` : `${formatOptionLabel(policy.subject_type)}: #${policy.subject_id || "-"}`;
}

function formatPermission(permission) {
  return [permission.module, permission.action, permission.field_key].filter(Boolean).join(" / ");
}

function getModuleActions(moduleName, permissionSchema) {
  const moduleMeta = permissionSchema[moduleName];
  if (!moduleMeta?.actions) return [];
  return Object.keys(moduleMeta.actions).map((value) => ({
    value,
    label: formatOptionLabel(value),
  }));
}

const OPTION_LABEL_OVERRIDES = {
  basic_details: "Basics",
  community: "Personal",
  education_career: "Career",
  address_contacts: "Address",
  family_details: "Family",
  family_relatives: "Relatives",
  media: "Media",
};

function formatOptionLabel(value) {
  const override = OPTION_LABEL_OVERRIDES[String(value || "").trim().toLowerCase()];
  if (override) return override;

  return String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function normalizeKeyInput(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function normalizeSearchValue(value) {
  return String(value || "").trim().toLowerCase();
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}

const C = {
  page: "#fff7f5",
  card: "#ffffff",
  border: "rgba(185,52,58,0.14)",
  borderSoft: "rgba(185,52,58,0.12)",
  borderStrong: "rgba(185,52,58,0.18)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  text: "#252935",
  muted: "#6b6f7d",
  mutedDark: "#5c6170",
  soft: "#fff7f5",
  softAlt: "#fffaf8",
  inkSoft: "#f3f4f6",
  successBg: "#eef8f1",
  successText: "#2f6b45",
};

const S = {
  page: {
    padding: 24,
    color: C.text,
    background: `linear-gradient(135deg, #ffffff 0%, ${C.page} 100%)`,
  },
  center: {
    padding: 48,
    textAlign: "center",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "center",
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 24,
    marginBottom: 18,
    boxShadow: "0 20px 48px rgba(37,41,53,0.07)",
  },
  eyebrow: {
    margin: 0,
    color: C.accentDark,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  title: {
    margin: "6px 0",
    fontSize: 30,
  },
  subtitle: {
    margin: 0,
    color: C.muted,
    maxWidth: 760,
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 18,
  },
  statCard: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    padding: 18,
    boxShadow: "0 12px 30px rgba(37,41,53,0.05)",
  },
  statValue: {
    fontSize: 26,
    fontWeight: 900,
  },
  statLabel: {
    color: C.muted,
    fontSize: 13,
  },
  tabs: {
    display: "flex",
    gap: 10,
    marginBottom: 18,
    flexWrap: "wrap",
  },
  tabBtn: {
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    padding: "10px 16px",
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 800,
    color: C.text,
  },
  tabBtnActive: {
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: C.card,
  },
  section: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 20,
    boxShadow: "0 18px 44px rgba(37,41,53,0.06)",
  },
  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 22,
  },
  sectionSub: {
    margin: "6px 0 0",
    color: C.muted,
  },
  dropdownToolbar: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 380px) minmax(0, 1fr)",
    gap: 14,
    alignItems: "center",
    marginBottom: 18,
  },
  dropdownSearchInput: {
    width: "100%",
    padding: "11px 12px",
    borderRadius: 12,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.text,
  },
  dropdownTypeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  dropdownTypeCard: {
    textAlign: "left",
    border: `1px solid ${C.border}`,
    background: C.soft,
    borderRadius: 20,
    padding: 18,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    boxShadow: "0 10px 26px rgba(37,41,53,0.04)",
  },
  dropdownTypeCardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
  },
  dropdownGroupPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: 999,
    background: C.card,
    border: `1px solid ${C.borderSoft}`,
    fontSize: 12,
    fontWeight: 800,
    color: C.accentDark,
  },
  dropdownCountPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 36,
    padding: "5px 10px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: C.card,
    fontSize: 12,
    fontWeight: 900,
  },
  dropdownTypeCardTitle: {
    fontSize: 18,
    fontWeight: 900,
  },
  dropdownTypeCardText: {
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.5,
  },
  dropdownTypeCardMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    fontSize: 12,
    color: C.mutedDark,
    fontWeight: 700,
  },
  emptyDropdownState: {
    marginTop: 14,
    padding: 18,
    borderRadius: 16,
    border: `1px dashed ${C.borderStrong}`,
    background: C.card,
    color: C.muted,
    textAlign: "center",
  },
  dualGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(320px, 0.9fr) minmax(0, 1.1fr)",
    gap: 18,
  },
  grid2Cols: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  accessGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 18,
    marginTop: 10,
    marginBottom: 18,
  },
  card: {
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    padding: 18,
    background: C.soft,
  },
  cardTitle: {
    marginTop: 0,
    marginBottom: 10,
    fontSize: 18,
  },
  buttonRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  diagnosticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
    marginTop: 18,
  },
  listRowCompact: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "8px 0",
    borderBottom: `1px solid ${C.borderSoft}`,
    fontSize: 14,
  },
  okText: {
    color: C.successText,
    fontWeight: 700,
    fontSize: 14,
  },
  warnBoxCompact: {
    padding: 12,
    borderRadius: 14,
    background: C.accentLight,
    border: `1px solid ${C.border}`,
    color: C.accentDark,
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 10,
  },
  infoBoxCompact: {
    padding: 12,
    borderRadius: 14,
    background: C.soft,
    border: `1px solid ${C.border}`,
    color: C.mutedDark,
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 10,
  },
  permissionChipWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  permissionChip: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 180,
    padding: 12,
    borderRadius: 14,
    background: C.card,
    border: `1px solid ${C.borderSoft}`,
    fontSize: 13,
    color: C.mutedDark,
  },
  field: {
    display: "block",
    marginBottom: 14,
  },
  label: {
    display: "block",
    marginBottom: 6,
    fontWeight: 700,
  },
  input: {
    width: "100%",
    padding: "11px 12px",
    borderRadius: 12,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.text,
  },
  inlineInput: {
    minWidth: 180,
    flex: "1 1 180px",
    padding: "10px 12px",
    borderRadius: 12,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.text,
  },
  inlineSelect: {
    minWidth: 180,
    flex: "1 1 180px",
    padding: "10px 12px",
    borderRadius: 12,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.text,
  },
  textarea: {
    width: "100%",
    minHeight: 88,
    padding: 12,
    borderRadius: 12,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.text,
    resize: "vertical",
  },
  primaryBtn: {
    border: "none",
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: C.card,
    borderRadius: 12,
    padding: "11px 16px",
    cursor: "pointer",
    fontWeight: 800,
  },
  secondaryBtn: {
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.text,
    borderRadius: 12,
    padding: "11px 16px",
    cursor: "pointer",
    fontWeight: 800,
  },
  deleteBtn: {
    border: `1px solid ${C.borderStrong}`,
    background: C.accentLight,
    color: C.accentDark,
    borderRadius: 12,
    padding: "9px 12px",
    cursor: "pointer",
    fontWeight: 800,
  },
  btnDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  actionRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  catalogFilterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 10,
  },
  catalogFilterActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "12px 10px",
    borderBottom: `1px solid ${C.border}`,
    color: C.muted,
    fontSize: 13,
  },
  thRight: {
    textAlign: "right",
    padding: "12px 10px",
    borderBottom: `1px solid ${C.border}`,
    color: C.muted,
    fontSize: 13,
  },
  td: {
    padding: "14px 10px",
    borderBottom: `1px solid ${C.borderSoft}`,
    verticalAlign: "top",
  },
  tdRight: {
    padding: "14px 10px",
    borderBottom: `1px solid ${C.borderSoft}`,
    textAlign: "right",
    verticalAlign: "top",
  },
  userName: {
    fontWeight: 800,
  },
  muted: {
    color: C.muted,
    fontSize: 13,
  },
  chipWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 10px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    cursor: "pointer",
    fontSize: 13,
    color: C.text,
  },
  chipButton: {
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    borderRadius: 999,
    padding: "8px 12px",
    cursor: "pointer",
    color: C.text,
  },
  chipActive: {
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: C.card,
    borderColor: C.accent,
  },
  chipDisabled: {
    opacity: 0.48,
    cursor: "not-allowed",
  },
  hiddenCheckbox: {
    display: "none",
  },
  listWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: 520,
    overflowY: "auto",
  },
  filtersCard: {
    display: "flex",
    alignItems: "flex-end",
    gap: 12,
    flexWrap: "wrap",
    padding: 16,
    borderRadius: 20,
    background: C.card,
    border: `1px solid ${C.borderSoft}`,
    marginBottom: 16,
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  filterLabel: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: C.accentDark,
  },
  readOnlyValue: {
    minHeight: 46,
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    borderRadius: 12,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.text,
  },
  hierarchyPanel: {
    display: "grid",
    gap: 12,
    padding: 14,
    borderRadius: 16,
    background: C.card,
    border: `1px solid ${C.borderSoft}`,
  },
  hierarchyPanelHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  hierarchyStatRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  hierarchyStatChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: C.soft,
    border: `1px solid ${C.borderStrong}`,
    color: C.accentDark,
    fontSize: 12,
    fontWeight: 800,
  },
  hierarchyPathWrap: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  hierarchyPathStep: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  hierarchyPathChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: C.soft,
    color: C.text,
    fontSize: 13,
    fontWeight: 700,
  },
  hierarchyPathChipActive: {
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: C.card,
    borderColor: C.accent,
  },
  hierarchyPathArrow: {
    color: C.mutedDark,
    fontWeight: 900,
  },
  hierarchyMatrixHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: 14,
  },
  hierarchyTopControls: {
    display: "grid",
    gap: 10,
    justifyItems: "end",
  },
  hierarchyViewSwitch: {
    display: "inline-flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
  },
  hierarchyViewBtn: {
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    color: C.text,
    borderRadius: 999,
    padding: "8px 14px",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: 13,
  },
  hierarchyViewBtnActive: {
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: C.card,
    borderColor: C.accent,
    boxShadow: "0 10px 24px rgba(185,52,58,0.18)",
  },
  hierarchyLegend: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  hierarchyLegendChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 34,
    padding: "0 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: `1px solid ${C.borderStrong}`,
  },
  hierarchyMatrixWrap: {
    overflowX: "auto",
    borderRadius: 18,
    border: `1px solid ${C.borderSoft}`,
    background: C.card,
  },
  hierarchyMatrixTable: {
    width: "100%",
    minWidth: 820,
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  hierarchyMatrixCorner: {
    position: "sticky",
    left: 0,
    zIndex: 4,
    minWidth: 180,
    padding: "12px 14px",
    textAlign: "left",
    background: "#fff8f6",
    color: C.text,
    borderBottom: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    fontSize: 12,
    fontWeight: 900,
  },
  hierarchyMatrixStickyLeft: {
    position: "sticky",
    left: 0,
  },
  hierarchyMatrixHeader: {
    minWidth: 92,
    maxWidth: 92,
    padding: "12px 10px",
    background: "#fff8f6",
    color: C.text,
    borderBottom: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.borderSoft}`,
    textAlign: "center",
    verticalAlign: "bottom",
  },
  hierarchyMatrixHeaderActive: {
    background: "#ffe5dd",
    color: C.accentDark,
  },
  hierarchyMatrixHeaderLabel: {
    display: "inline-block",
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    whiteSpace: "nowrap",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.02em",
  },
  hierarchyMatrixRowHeader: {
    zIndex: 3,
    minWidth: 180,
    padding: "10px 14px",
    textAlign: "left",
    background: C.card,
    borderBottom: `1px solid ${C.borderSoft}`,
    borderRight: `1px solid ${C.border}`,
    fontSize: 13,
    fontWeight: 800,
  },
  hierarchyRowRole: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  hierarchyIndentGuide: {
    color: C.accent,
    opacity: 0.55,
    letterSpacing: "0.08em",
    whiteSpace: "pre",
  },
  hierarchyMatrixCell: {
    width: 92,
    minWidth: 92,
    height: 48,
    padding: "0 8px",
    textAlign: "center",
    borderBottom: `1px solid ${C.borderSoft}`,
    borderRight: `1px solid ${C.borderSoft}`,
    fontSize: 12,
    fontWeight: 900,
  },
  hierarchyCellEmpty: {
    background: C.card,
    color: C.muted,
  },
  hierarchyCellSelf: {
    background: "#ffe8e1",
    color: C.accentDark,
  },
  hierarchyCellDirect: {
    background: "#fce4de",
    color: "#9f372f",
  },
  hierarchyCellIndirect: {
    background: "#fff4ef",
    color: C.mutedDark,
  },
  hierarchyCellSelectedAxis: {
    boxShadow: "inset 0 0 0 1px rgba(185,52,58,0.18)",
  },
  hierarchyTreeWrap: {
    display: "grid",
    gap: 14,
  },
  hierarchyTreeSummary: {
    padding: 14,
    borderRadius: 16,
    background: C.softAlt,
    border: `1px solid ${C.borderSoft}`,
    color: C.mutedDark,
    fontSize: 14,
    fontWeight: 700,
  },
  hierarchyTreeViewport: {
    overflowX: "auto",
    paddingBottom: 8,
  },
  hierarchyForest: {
    display: "inline-flex",
    gap: 36,
    alignItems: "flex-start",
    justifyContent: "center",
    minWidth: "100%",
    padding: "8px 18px 18px",
    boxSizing: "border-box",
  },
  orgTreeNode: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "max-content",
  },
  orgTreeCard: {
    width: 220,
    minHeight: 88,
    padding: 16,
    borderRadius: 18,
    background: C.card,
    border: `1px solid ${C.borderSoft}`,
    boxShadow: "0 10px 24px rgba(37,41,53,0.05)",
    boxSizing: "border-box",
    textAlign: "center",
  },
  orgTreeCardActive: {
    borderColor: C.accent,
    boxShadow: "0 14px 30px rgba(185,52,58,0.16)",
    background: "#fff9f7",
  },
  orgTreeCardTop: {
    display: "grid",
    gap: 10,
    justifyItems: "center",
  },
  orgTreeRoleName: {
    fontSize: 16,
    fontWeight: 900,
    color: C.text,
    lineHeight: 1.25,
  },
  orgTreeDepthChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: C.soft,
    border: `1px solid ${C.borderStrong}`,
    color: C.accentDark,
    fontSize: 12,
    fontWeight: 800,
  },
  orgTreeMeta: {
    marginTop: 10,
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  orgTreeChildrenWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "max-content",
  },
  orgTreeStem: {
    width: 2,
    height: 22,
    background: C.borderStrong,
  },
  orgTreeChildrenRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    width: "max-content",
    gap: 18,
  },
  orgTreeBranch: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 220,
    width: "max-content",
  },
  orgTreeBranchHead: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  orgTreeBranchLink: {
    width: "100%",
    height: 18,
    borderTop: `2px solid ${C.borderStrong}`,
    boxSizing: "border-box",
  },
  orgTreeBranchLinkFirst: {
    width: "50%",
    marginLeft: "50%",
  },
  orgTreeBranchLinkLast: {
    width: "50%",
    marginRight: "50%",
  },
  orgTreeBranchStem: {
    width: 2,
    height: 18,
    background: C.borderStrong,
  },
  listRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    padding: 12,
    borderRadius: 14,
    background: C.card,
    border: `1px solid ${C.borderSoft}`,
  },
  emptyStateBox: {
    border: `1px dashed ${C.borderStrong}`,
    background: C.soft,
    borderRadius: 14,
    padding: 18,
    textAlign: "center",
    color: C.muted,
    fontSize: 14,
    fontWeight: 700,
  },
  policyCard: {
    border: `1px solid ${C.borderSoft}`,
    borderRadius: 18,
    padding: 14,
    background: C.card,
  },
  verificationCard: {
    border: `1px solid ${C.borderSoft}`,
    borderRadius: 20,
    padding: 16,
    background: C.card,
    boxShadow: "0 10px 24px rgba(37,41,53,0.04)",
  },
  verificationCardHead: {
    marginBottom: 14,
  },
  verificationIdentityRow: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
  },
  verificationPhotoWrap: {
    width: 92,
    height: 112,
    borderRadius: 18,
    overflow: "hidden",
    background: C.soft,
    flexShrink: 0,
    border: `1px solid ${C.borderSoft}`,
  },
  verificationPhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  verificationAvatar: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #f7c8be, #ef8f7e)",
  },
  verificationAvatarText: {
    fontSize: 28,
    fontWeight: 900,
    color: C.card,
  },
  verificationIdentityBody: {
    flex: 1,
    minWidth: 0,
    display: "grid",
    gap: 6,
  },
  verificationMetaLine: {
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.5,
  },
  verificationPillRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 4,
  },
  verificationInfoPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: C.soft,
    border: `1px solid ${C.borderSoft}`,
    color: C.text,
    fontSize: 12,
    fontWeight: 700,
  },
  verificationDetailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 14,
  },
  verificationInfoCard: {
    padding: 12,
    borderRadius: 16,
    background: C.soft,
    border: `1px solid ${C.borderSoft}`,
  },
  verificationInfoLabel: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: C.accentDark,
    marginBottom: 6,
  },
  verificationInfoValue: {
    color: C.text,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  policyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  dropdownRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    padding: 12,
    borderRadius: 14,
    background: C.card,
    border: `1px solid ${C.borderSoft}`,
  },
  rowTitle: {
    fontWeight: 800,
  },
  selectorList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: 420,
    overflowY: "auto",
    background: C.card,
    borderRadius: 14,
    padding: 12,
    border: `1px solid ${C.borderSoft}`,
  },
  selectorItem: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    fontSize: 14,
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    margin: "6px 0 12px",
  },
  subTitle: {
    marginTop: 0,
    marginBottom: 10,
  },
  helperText: {
    margin: "0 0 10px",
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.5,
  },
  policyBuilder: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginBottom: 12,
  },
  policyBuilderHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
  },
  multiValueSelect: {
    width: "100%",
    padding: 12,
    borderRadius: 12,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    minHeight: 120,
    color: C.text,
  },
  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },
  checkboxRowCompact: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: C.text,
  },
  badge: {
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
  },
  badgeActive: {
    background: C.successBg,
    color: C.successText,
  },
  badgeMuted: {
    background: C.inkSoft,
    color: C.mutedDark,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.24)",
    display: "grid",
    placeItems: "center",
    padding: 20,
    zIndex: 1000,
  },
  modal: {
    width: "min(640px, 100%)",
    background: C.card,
    borderRadius: 20,
    padding: 20,
  },
  dropdownModal: {
    width: "min(1160px, 100%)",
    maxHeight: "92vh",
    overflow: "hidden",
    background: C.softAlt,
    borderRadius: 24,
    padding: 22,
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  dropdownModalHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
  },
  dropdownModalEyebrow: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: C.accentDark,
    marginBottom: 6,
  },
  dropdownModalTitle: {
    margin: 0,
    fontSize: 26,
  },
  dropdownModalSub: {
    margin: "8px 0 0",
    color: C.muted,
    lineHeight: 1.5,
  },
  dropdownModalStats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 12,
  },
  dropdownModalStatCard: {
    borderRadius: 18,
    padding: 16,
    background: "linear-gradient(135deg, #ffffff 0%, #fff0eb 100%)",
    border: `1px solid ${C.borderSoft}`,
  },
  dropdownModalStatValue: {
    fontSize: 24,
    fontWeight: 900,
    color: C.text,
  },
  dropdownModalStatLabel: {
    marginTop: 4,
    fontSize: 12,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 800,
  },
  dropdownModalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 18,
    minHeight: 0,
    overflow: "hidden",
  },
  dropdownListHead: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(260px, 340px)",
    gap: 14,
    alignItems: "end",
    marginBottom: 14,
  },
  dropdownEditorList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    maxHeight: "62vh",
    overflowY: "auto",
    paddingRight: 4,
  },
  dropdownEditorCard: {
    border: `1px solid ${C.borderSoft}`,
    borderRadius: 18,
    background: C.card,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  dropdownEditorHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },
  dropdownEditorFields: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },
  cityTierManager: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    gridColumn: "1 / -1",
    paddingTop: 4,
  },
  cityTierManagerHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  cityTierPanelGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 14,
  },
  cityTierPanel: {
    borderRadius: 18,
    border: `1px solid ${C.borderSoft}`,
    background: C.softAlt,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minHeight: 0,
  },
  cityTierPanelHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },
  cityTierPanelTitle: {
    fontSize: 15,
    fontWeight: 900,
    color: C.text,
  },
  cityTierCountBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 44,
    padding: "6px 10px",
    borderRadius: 999,
    background: "#fce4dd",
    color: C.accentDark,
    fontSize: 12,
    fontWeight: 900,
  },
  cityTierList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: 320,
    overflowY: "auto",
    paddingRight: 4,
  },
  cityTierListRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    padding: 12,
    borderRadius: 14,
    background: C.card,
    border: `1px solid ${C.borderSoft}`,
  },
  cityTierSelectedRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    padding: 12,
    borderRadius: 14,
    background: C.soft,
    border: `1px solid ${C.border}`,
  },
  cityTierListCopy: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  cityTierLocation: {
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.45,
  },
  cityTierEmptyState: {
    borderRadius: 14,
    border: `1px dashed ${C.border}`,
    background: C.card,
    padding: 18,
    textAlign: "center",
    color: C.muted,
  },
  ghostBtn: {
    border: "none",
    background: "transparent",
    color: C.accentDark,
    padding: 0,
    cursor: "pointer",
    fontWeight: 800,
  },
  errorBox: {
    marginBottom: 14,
    padding: 12,
    borderRadius: 14,
    background: C.accentLight,
    color: C.accentDark,
    border: `1px solid ${C.border}`,
  },
  successBox: {
    marginBottom: 14,
    padding: 12,
    borderRadius: 14,
    background: C.successBg,
    color: C.successText,
    border: "1px solid #cfe6d7",
  },
};

